import fetch from "isomorphic-fetch";
import IdealStudio from "../core/editor/main";
import i18n from "i18next";
import { ActionCreators } from "redux-undo";
import {
  ID_PREFIX_FILE,
  FILE_UPLOAD_ERROR,
  FILE_UPLOADING,
  FILE_DELETING,
  FILE_DELETE_ERROR,
} from "./constants";
import { isDataURL } from "./utils";
import { isYoutubeUrl, fetchYoutubeDuration } from "./youtube_tools";
import { getEditorPlayhead } from "./playhead_tracker.es6";
import { mandatoryDependencyMissing, MANDATORY_RESET } from "./scene_mandatory";
import { serialize } from "../reducers/serializer";
import { getToken, getUserId } from "./environment_config";
import { deleteDraft, saveDraftFromState } from "./localDraftStore.es6";
import { apiGet, apiPut } from "./http_service";
import {
  triggerAuthRequired,
  isEditorLoaded,
  markEditorLoaded,
  isAuthRequiredTriggered,
} from "../_editor/utils/authRequired";
import {
  buildBaseUpdatedAtField,
  updateBaseUpdatedAtFromResponse,
  parseSaveConflict,
} from "./saveConflict";
import { FPS, parseTimeStr } from "./sceneTimeline.es6";
import { newSoundId } from "./sound_id.es6";
import { isFileTooLarge } from "./upload_limits.es6";
import {
  classifyPluginTrackType,
  planSharedRowPlacement,
  pickRowForSpan,
  isTrackSpanFree,
  DEFAULT_IMAGE_DURATION_FRAMES,
} from "./media_track_types.es6";
export const ADD_BOX = "ADD_BOX";
export const SELECT_BOX = "SELECT_BOX";
export const SELECT_BOX_ALL = "SELECT_BOX_ALL";

export const MOVE_BOX = "MOVE_BOX";
export const CHANGE_START_ANIMATION = "CHANGE_START_ANIMATION";
export const CHANGE_MIDDLE_ANIMATION = "CHANGE_MIDDLE_ANIMATION";
export const CHANGE_END_ANIMATION = "CHANGE_END_ANIMATION";
export const CHANGE_FINAL_ANIMATION = "CHANGE_FINAL_ANIMATION";
export const CHANGE_ANIMATION_POSITION_NUMBER = "CHANGE_ANIMATION_POSITION_NUMBER";
export const CHANGE_SHOW_ANIMATION = "CHANGE_SHOW_ANIMATION";
export const CHANGE_ANIMATION_DURATIONS = "CHANGE_ANIMATION_DURATIONS";

export const RESIZE_BOX = "RESIZE_BOX";
export const ROTATE_BOX = "ROTATE_BOX";
export const UPDATE_BOX = "UPDATE_BOX";
export const UPDATE_BOX_TIMELINE = "UPDATE_BOX_TIMELINE";
// Legacy media repair: one batched write of {boxId: timelinePatch} pairs, so
// stamping a whole training's video/audio boxes costs a single undo entry.
export const REPAIR_MEDIA_TIMELINES = "REPAIR_MEDIA_TIMELINES";
// Interactive Video MVP (phase 1): array CRUD on box.timeline.interactions.
// Each interaction = { id, startTime, endTime, kind, params, position }.
// Lives on the VideoBox itself (no new plugin), consumed by Visor runtime
// to fire onVideoTimeReached / show VideoOverlay sub-items.
export const UPDATE_VIDEO_INTERACTIONS = "UPDATE_VIDEO_INTERACTIONS";
export const DELETE_BOX = "DELETE_BOX";
export const DROP_BOX = "DROP_BOX";
export const VERTICALLY_ALIGN_BOX = "VERTICALLY_ALIGN_BOX";
export const INCREASE_LEVEL = "INCREASE_LEVEL";
// this is to move a box that has relative position inside a container
export const REORDER_BOXES = "REORDER_BOXES";

export const RESIZE_SORTABLE_CONTAINER = "RESIZE_SORTABLE_CONTAINER";
export const DELETE_SORTABLE_CONTAINER = "DELETE_SORTABLE_CONTAINER";
export const REORDER_SORTABLE_CONTAINER = "REORDER_SORTABLE_CONTAINER";
export const CHANGE_SORTABLE_PROPS = "CHANGE_SORTABLE_PROPS";
export const CHANGE_COLS = "CHANGE_COLS";
export const CHANGE_ROWS = "CHANGE_ROWS";

export const ADD_NAV_ITEM = "ADD_NAV_ITEM";
export const ADD_NAV_ITEMS = "ADD_NAV_ITEMS";
export const SELECT_NAV_ITEM = "SELECT_NAV_ITEM";
export const EXPAND_NAV_ITEM = "EXPAND_NAV_ITEM";
export const DELETE_NAV_ITEM = "DELETE_NAV_ITEM";
export const REORDER_NAV_ITEM = "REORDER_NAV_ITEM";
export const TOGGLE_NAV_ITEM = "TOGGLE_NAV_ITEM";
export const CHANGE_NAV_ITEM_NAME = "CHANGE_NAV_ITEM_NAME";
export const CHANGE_NAV_ITEM_DURATION = "CHANGE_NAV_ITEM_DURATION";
export const CHANGE_BACKGROUND = "CHANGE_BACKGROUND";
export const CHANGE_THEME_PALETTE = "CHANGE_THEME_PALETTE";
export const CHANGE_THEME_PALETTE_ALL = "CHANGE_THEME_PALETTE_ALL";
export const BATCH_UPDATE_MANDATORY_DEFAULTS = "BATCH_UPDATE_MANDATORY_DEFAULTS";
export const BATCH_UPDATE_SCENE_DURATION = "BATCH_UPDATE_SCENE_DURATION";
export const BATCH_UPDATE_NARRATION_VOICE = "BATCH_UPDATE_NARRATION_VOICE";
export const BATCH_SET_SCENE_FONT = "BATCH_SET_SCENE_FONT";

export const INDEX_SELECT = "INDEX_SELECT";

export const TOGGLE_TEXT_EDITOR = "TOGGLE_TEXT_EDITOR";
export const TOGGLE_TITLE_MODE = "TOGGLE_TITLE_MODE";
export const CHANGE_DISPLAY_MODE = "CHANGE_DISPLAY_MODE";
export const SET_BUSY = "SET_BUSY";
export const DUPLICATE_NAV_ITEM = "DUPLICATE_NAV_ITEM";
export const DUPLICATE_NAV_ITEM_BY_PAYLOAD = "DUPLICATE_NAV_ITEM_BY_PAYLOAD";
export const UPDATE_NAV_ITEM_EXTRA_FILES = "UPDATE_NAV_ITEM_EXTRA_FILES";
export const PASTE_SCENE = "PASTE_SCENE";

export const CONFIG_SCORE = "CONFIG_SCORE";
export const UPDATE_PLUGIN_TOOLBAR = "UPDATE_PLUGIN_TOOLBAR";
export const UPDATE_VIEW_TOOLBAR = "UPDATE_VIEW_TOOLBAR";
// Bulk image-URL swap after "apply logo to all training images": rewrites
// pluginToolbarsById[*].state.(url|src) + viewToolbarsById[*].background from a
// urlMap { oldUrl: newUrl } so the canvas reflects the newly logoed images.
export const APPLY_IMAGE_URL_MAP = "APPLY_IMAGE_URL_MAP";

export const IMPORT_EDI = "IMPORT_EDI";
export const IMPORT_STATE = "IMPORT_STATE";
export const CHANGE_GLOBAL_CONFIG = "CHANGE_GLOBAL_CONFIG";

// Aidea Smart Elements (image decomposition into editable layers)
export const APPLY_AIDEA_SMART_ELEMENTS = "APPLY_AIDEA_SMART_ELEMENTS";
export const SET_AIDEA_SMART_ELEMENTS_PROCESSING = "SET_AIDEA_SMART_ELEMENTS_PROCESSING";
export const CLEAR_AIDEA_SMART_ELEMENTS_PROCESSING = "CLEAR_AIDEA_SMART_ELEMENTS_PROCESSING";

export function applyAideaSmartElements(layerSet) {
  return { type: APPLY_AIDEA_SMART_ELEMENTS, payload: layerSet };
}
export function setAideaSmartElementsProcessing(boxId, info) {
  return { type: SET_AIDEA_SMART_ELEMENTS_PROCESSING, payload: { boxId, info } };
}
export function clearAideaSmartElementsProcessing(boxId) {
  return { type: CLEAR_AIDEA_SMART_ELEMENTS_PROCESSING, payload: { boxId } };
}

export const UNGROUP_AIDEA_SMART_ELEMENT = "UNGROUP_AIDEA_SMART_ELEMENT";

export function ungroupAideaSmartElement(boxId) {
  return { type: UNGROUP_AIDEA_SMART_ELEMENT, payload: { boxId } };
}

export const ADD_RICH_MARK = "ADD_RICH_MARK";
export const EDIT_RICH_MARK = "EDIT_RICH_MARK";
export const MOVE_RICH_MARK = "MOVE_RICH_MARK";
export const DELETE_RICH_MARK = "DELETE_RICH_MARK";

export const ADD_CONTAINED_VIEW = "ADD_CONTAINED_VIEW";
export const SELECT_CONTAINED_VIEW = "SELECT_CONTAINED_VIEW";
export const DELETE_CONTAINED_VIEW = "DELETE_CONTAINED_VIEW";
export const CHANGE_CONTAINED_VIEW_NAME = "CHANGE_CONTAINED_VIEW_NAME";

export const UPLOAD_FILE = "UPLOAD_FILE";
export const DELETE_FILE = "DELETE_FILE";

export const EDIT_PLUGIN_TEXT = "EDIT_PLUGIN_TEXT";

export const PASTE_BOX = "PASTE_BOX";
export const CHANGE_BOX_LAYER = "CHANGE_BOX_LAYER";

export const SET_CORRECT_ANSWER = "SET_CORRECT_ANSWER";

export const UPLOAD_TEMPLATE = "UPLOAD_TEMPLATE";
export const DELETE_TEMPLATE = "DELETE_TEMPLATE";

export const ADD_SOUND = "ADD_SOUND";
export const EDIT_SOUND = "EDIT_SOUND";
export const DELETE_SOUND = "DELETE_SOUND";
export const SET_SCENE_AUDIO_ROW_ORDERS = "SET_SCENE_AUDIO_ROW_ORDERS";

export const ADD_ANIMATION = "ADD_ANIMATION";
export const SAVE_ANIMATION = "SAVE_ANIMATION";
export const DELETE_ANIMATION = "DELETE_ANIMATION";
export const UPDATE_TEXT_ANIMATION = "UPDATE_TEXT_ANIMATION";
export const UPDATE_BOX_ANIMATION = "UPDATE_BOX_ANIMATION";
export const UPDATE_BOX_EXIT_ANIMATION = "UPDATE_BOX_EXIT_ANIMATION";
export const DELETE_TEXT_ANIMATION = "DELETE_TEXT_ANIMATION";
export const DELETE_BOX_ANIMATION = "DELETE_BOX_ANIMATION";
export const DELETE_BOX_EXIT_ANIMATION = "DELETE_BOX_EXIT_ANIMATION";

export const SYNC_SCENE_SUCCESS = "SYNC_SCENE_SUCCESS";

export const SET_GUIDE_TAG = "SET_GUIDE_TAG";
export const CLEAR_GUIDE_TAGS = "CLEAR_GUIDE_TAGS";
// Handled by boxesById, pluginToolbarsById, and navItemsById reducers (Task 8) and dispatched as a thunk by `migrateLegacyGuides` (Task 9).
export const MIGRATE_LEGACY_GUIDES = "MIGRATE_LEGACY_GUIDES";
// Handled by boxesById, pluginToolbarsById, navItemsById and containedViewsById reducers.
export const MIGRATE_LEGACY_SOUND_LISTS = "MIGRATE_LEGACY_SOUND_LISTS";

export function changeNavItemDuration(id, duration) {
  return { type: CHANGE_NAV_ITEM_DURATION, payload: { id, duration } };
}

export function syncSceneSuccess(boxesById, pluginToolbarsById) {
  return { type: SYNC_SCENE_SUCCESS, payload: { boxesById, pluginToolbarsById } };
}

export function selectIndex(id) {
  return { type: INDEX_SELECT, payload: { id } };
}

export function selectNavItem(id) {
  return { type: SELECT_NAV_ITEM, payload: { id } };
}

export function addContainedView(id, name, container) {
  return { type: ADD_CONTAINED_VIEW, payload: { id, name, container } };
}

export function deleteContainedView(ids, boxes, parent) {
  return { type: DELETE_CONTAINED_VIEW, payload: { ids, boxes, parent } };
}

export function addNavItem(
  id,
  name,
  parent,
  type,
  position,
  background,
  customSize,
  hideTitles,
  hasContent,
  sortable_id,
  navItemSelected = null
) {
  return {
    type: ADD_NAV_ITEM,
    payload: {
      id,
      name,
      parent,
      type,
      position,
      background,
      customSize,
      hideTitles,
      hasContent,
      sortable_id,
      navItemSelected,
    },
  };
}

export function addNavItems(navs, parent) {
  return { type: ADD_NAV_ITEMS, payload: { navs, parent } };
}

export function expandNavItem(id, value) {
  return { type: EXPAND_NAV_ITEM, payload: { id, value } };
}

export function deleteNavItem(ids, parent, boxes, containedViews, linkedBoxes) {
  return { type: DELETE_NAV_ITEM, payload: { ids, parent, boxes, containedViews, linkedBoxes } };
}

export function reorderNavItem(id, newParent, oldParent, idsInOrder, childrenInOrder) {
  return {
    type: REORDER_NAV_ITEM,
    payload: { id, newParent, oldParent, idsInOrder, childrenInOrder },
  };
}

export function toggleNavItem(id) {
  return { type: TOGGLE_NAV_ITEM, payload: { id } };
}

export function changeBackground(id, background) {
  return { type: CHANGE_BACKGROUND, payload: { id, background } };
}

export function changeThemePalette(id, themeId, paletteId, themeHtml, themeCssVariables = {}) {
  return {
    type: CHANGE_THEME_PALETTE,
    payload: { id, themeId, paletteId, themeHtml, themeCssVariables },
  };
}

export function changeThemePaletteAll(themeId, paletteId, themeHtml, themeCssVariables = {}) {
  return {
    type: CHANGE_THEME_PALETTE_ALL,
    payload: { themeId, paletteId, themeHtml, themeCssVariables },
  };
}

// question = { mandatory, sceneIds }: payload for scenes with questions.
export function batchUpdateMandatoryDefaults(mandatory, question) {
  return { type: BATCH_UPDATE_MANDATORY_DEFAULTS, payload: { mandatory, question } };
}

export function batchUpdateSceneDuration(duration) {
  return { type: BATCH_UPDATE_SCENE_DURATION, payload: { duration } };
}

export function batchUpdateNarrationVoice(voiceData) {
  return { type: BATCH_UPDATE_NARRATION_VOICE, payload: { voiceData } };
}

// Course-wide font by default: every BasicText/RichText box, every scene
// toolbar, plus globalConfig.fontFamily as the new course default.
// `scope` narrows it to one scene (the canvas font button's "this scene"
// option): pass `{ boxIds, navItemId }` and the course default is left alone.
export function batchSetSceneFont(fontFamily, scope) {
  return { type: BATCH_SET_SCENE_FONT, payload: { fontFamily, ...(scope || {}) } };
}

export function addBox(ids, draggable, resizable, content, style, state, structure, initialParams) {
  // Insert-at-playhead fallback for flows that dispatch addBox directly
  // (addBoxThunk injects before calling this, making it a no-op there).
  // Direct parent/page match only — no getState for the container-chain walk.
  const suppliedTl = (initialParams && initialParams.timeline) || (ids && ids.timeline) || {};
  if (typeof suppliedTl.startFrame !== "number") {
    const playhead = getEditorPlayhead();
    if (
      playhead &&
      playhead.frame > 0 &&
      ids &&
      (ids.parent === playhead.sceneId || ids.page === playhead.sceneId)
    ) {
      initialParams = {
        ...(initialParams || {}),
        timeline: { ...suppliedTl, startFrame: Math.round(playhead.frame) },
      };
    }
  }
  return {
    type: ADD_BOX,
    payload: { ids, draggable, resizable, content, style, state, structure, initialParams },
  };
}

function resolveSceneIdForIds(present, ids) {
  if (!ids) return null;
  if (ids.page != null && ids.page !== 0) return ids.page;
  let cur = ids.parent;
  for (let hops = 0; cur != null && hops < 20; hops++) {
    if (
      (present.navItemsById && present.navItemsById[cur]) ||
      (present.containedViewsById && present.containedViewsById[cur])
    ) {
      return cur;
    }
    const parentBox = present.boxesById && present.boxesById[cur];
    if (!parentBox) return null;
    cur = parentBox.parent;
  }
  return null;
}

// Adds a box, then — for a newly-added EnrichedPlayer video with no explicit
// timeline.endFrame — persists an endFrame equal to the media's natural duration
// once its metadata loads. Without this, a video that is never touched on the
// timeline has a zero-length span in buildSceneTimeline (so it is dropped from
// the scene timeline → waitForVideo/scene-duration ignore it, falling back to the
// min-time floor) and no visibility end (it lingers / re-appears on scene
// restart). Route every "add box" path through this so the fix is reliable.
// Scoped to real adds (ADD_BOX) — content loaded via IMPORT_STATE is untouched.
export function addBoxThunk(
  ids,
  draggable,
  resizable,
  content,
  style,
  state,
  structure,
  initialParams
) {
  return (dispatch, getState) => {
    // ── Insert at the playhead ─────────────────────────────────────────
    // A new element lands at the playhead unless the caller supplied an
    // explicit startFrame, the playhead is at 0, or the timeline shows a
    // different scene. Parent may be the scene (slides) or a container box
    // chain (documents) — walk up to decide.
    const belongsToScene = (parentId, sceneId) => {
      const present = (getState() && getState().undoGroup && getState().undoGroup.present) || {};
      let cur = parentId;
      for (let hops = 0; cur != null && hops < 20; hops++) {
        if (cur === sceneId) return true;
        const parentBox = present.boxesById && present.boxesById[cur];
        if (!parentBox) return false;
        cur = parentBox.parent;
      }
      return false;
    };
    const suppliedTimeline =
      (initialParams && initialParams.timeline) || (ids && ids.timeline) || {};
    const pluginName =
      (ids && ids.config && ids.config.name) || (initialParams && initialParams.name) || "";
    let insertStartFrame =
      typeof suppliedTimeline.startFrame === "number" ? suppliedTimeline.startFrame : 0;

    // Place unpositioned shareable media at the playhead and join a free type row.
    const trackType = classifyPluginTrackType(pluginName);
    const durationHint =
      typeof suppliedTimeline.durationHintFrames === "number" &&
      suppliedTimeline.durationHintFrames > 0
        ? Math.max(3, Math.round(suppliedTimeline.durationHintFrames))
        : null;
    let placedByTrackPolicy = false;
    // Row the element joins, plus the existing boxes that must be stamped with
    // that row id for the join to be visible (adoption of an ungrouped box).
    let joinedTrackId = null;
    let adoptIds = [];
    if (trackType && typeof suppliedTimeline.startFrame !== "number") {
      const present = (getState() && getState().undoGroup && getState().undoGroup.present) || {};
      const sceneId = resolveSceneIdForIds(present, ids);
      const scene =
        (sceneId && present.navItemsById && present.navItemsById[sceneId]) ||
        (sceneId && present.containedViewsById && present.containedViewsById[sceneId]);
      const sceneBoxIds = (scene && scene.boxes) || [];

      insertStartFrame = 0;
      const playhead = getEditorPlayhead();
      if (
        playhead &&
        playhead.frame > 0 &&
        ids &&
        (belongsToScene(ids.parent, playhead.sceneId) ||
          (ids.page != null && ids.page === playhead.sceneId))
      ) {
        insertStartFrame = Math.round(playhead.frame);
      }

      const estimatedLen = durationHint != null ? durationHint : DEFAULT_IMAGE_DURATION_FRAMES;
      // Any same-type row with room takes the element — including a legacy /
      // ungrouped box, which gets adopted into a track so the two merge
      // instead of the add opening yet another row above everything.
      const placement = planSharedRowPlacement({
        boxesById: present.boxesById || {},
        sceneBoxIds,
        // Same source the timeline's getLayerType reads, so the two agree.
        pluginIdOf: (id) =>
          ((present.pluginToolbarsById && present.pluginToolbarsById[id]) || {}).pluginId ||
          ((present.boxesById && present.boxesById[id]) || {}).pluginId,
        type: trackType,
        startFrame: insertStartFrame,
        endFrame: insertStartFrame + estimatedLen,
      });
      joinedTrackId = placement.trackId;
      adoptIds = placement.adoptIds;
      const defaultEndFrame =
        durationHint != null
          ? insertStartFrame + durationHint
          : trackType === "image" || trackType === "text"
            ? insertStartFrame + DEFAULT_IMAGE_DURATION_FRAMES
            : null; // video/audio without hint: media probe below persists it
      const { durationHintFrames, ...restTimeline } = suppliedTimeline; // eslint-disable-line no-unused-vars
      initialParams = {
        ...(initialParams || {}),
        timeline: {
          visible: true,
          ...restTimeline,
          startFrame: insertStartFrame,
          ...(defaultEndFrame != null ? { endFrame: defaultEndFrame } : {}),
          ...(joinedTrackId ? { trackId: joinedTrackId } : {}),
        },
      };
      placedByTrackPolicy = true;
    }

    if (!placedByTrackPolicy && typeof suppliedTimeline.startFrame !== "number") {
      const playhead = getEditorPlayhead();
      if (
        playhead &&
        playhead.frame > 0 &&
        ids &&
        (belongsToScene(ids.parent, playhead.sceneId) ||
          (ids.page != null && ids.page === playhead.sceneId))
      ) {
        insertStartFrame = Math.round(playhead.frame);
        initialParams = {
          ...(initialParams || {}),
          timeline: { ...suppliedTimeline, startFrame: insertStartFrame },
        };
      }
    }

    if (!placedByTrackPolicy && durationHint != null) {
      const tl = (initialParams && initialParams.timeline) || suppliedTimeline;
      const { durationHintFrames, ...restTimeline } = tl; // eslint-disable-line no-unused-vars
      initialParams = {
        ...(initialParams || {}),
        timeline: {
          ...restTimeline,
          ...(typeof restTimeline.endFrame === "number"
            ? {}
            : { endFrame: insertStartFrame + durationHint }),
        },
      };
    }

    adoptIds.forEach((id) => dispatch(updateBoxTimeline(id, { trackId: joinedTrackId })));

    dispatch(addBox(ids, draggable, resizable, content, style, state, structure, initialParams));

    const boxId = ids && ids.id;
    const url = state && state.url;

    // Non-media elements have no natural duration: freeze the scene's current
    // duration (the it-duration-marker position) into timeline.endFrame at
    // insertion. Every element then owns a fixed endFrame and the marker never
    // retro-affects element lifetimes. Media plugins are skipped — their end
    // derives from the media file (EnrichedPlayer persists it below).
    const isMediaPlugin = /video|player|audio|sound/i.test(pluginName);
    const finalTimeline = (initialParams && initialParams.timeline) || suppliedTimeline;
    const hasExplicitEnd = typeof finalTimeline.endFrame === "number";
    if (boxId && !isMediaPlugin && !hasExplicitEnd) {
      const present = (getState() && getState().undoGroup && getState().undoGroup.present) || {};
      const parentId = ids && ids.parent;
      const nav =
        (present.navItemsById && present.navItemsById[parentId]) ||
        (present.containedViewsById && present.containedViewsById[parentId]);
      const durF = nav && typeof nav.duration === "number" && nav.duration > 0 ? nav.duration : 0;
      const startF = insertStartFrame;
      // No scene duration yet (fresh scene): a fixed 5s default, so EVERY new
      // element owns an endFrame from birth and never stretches with content.
      const endFrame = durF > 0 ? Math.max(startF + 30, Math.round(durF)) : startF + 150;
      dispatch(updateBoxTimeline(boxId, { endFrame }));
    }

    const persistEndFrame = (durationSec) => {
      if (!Number.isFinite(durationSec) || durationSec <= 0) return;
      const present = (getState() && getState().undoGroup && getState().undoGroup.present) || {};
      const box = present.boxesById && present.boxesById[boxId];
      if (!box) return; // removed before the duration arrived
      if (box.timeline && typeof box.timeline.endFrame === "number") return; // already set
      // Offset by the box's own start so a playhead-inserted video spans
      // [startFrame, startFrame + media length], not [0, media length].
      const sf = (box.timeline && box.timeline.startFrame) || 0;
      const endFrame = sf + Math.ceil(durationSec * 30);
      // The row was chosen against an ESTIMATED length; the real one may run
      // into the next clip. Overlap inside a row is not a legal state, so the
      // clip leaves the row instead of being silently trimmed.
      const rowId = box.timeline && box.timeline.trackId;
      let leavesRow = false;
      if (typeof rowId === "string") {
        const sceneId = resolveSceneIdForIds(present, ids);
        const scene =
          (sceneId && present.navItemsById && present.navItemsById[sceneId]) ||
          (sceneId && present.containedViewsById && present.containedViewsById[sceneId]);
        const others = ((scene && scene.boxes) || []).filter((id) => id !== boxId);
        leavesRow = !isTrackSpanFree(present.boxesById || {}, others, rowId, sf, endFrame);
      }
      dispatch(
        updateBoxTimeline(boxId, { endFrame, ...(leavesRow ? { trackId: undefined } : {}) })
      );
    };

    if (trackType === "audio" && boxId && url && !hasExplicitEnd) {
      try {
        const probe = new Audio();
        const onMeta = () => {
          probe.removeEventListener("loadedmetadata", onMeta);
          persistEndFrame(probe.duration);
        };
        probe.addEventListener("loadedmetadata", onMeta);
        probe.addEventListener("error", () => probe.removeEventListener("loadedmetadata", onMeta));
        probe.preload = "metadata";
        probe.src = url;
      } catch (e) {
        /* ignore */
      }
      return;
    }

    if (pluginName !== "EnrichedPlayer" || !boxId || !url) return;
    if (hasExplicitEnd) return;

    // A YouTube embed URL is a page, not a media file — a <video> probe errors
    // on it. Resolve the duration through the Data API instead.
    if (isYoutubeUrl(url)) {
      fetchYoutubeDuration(url).then(persistEndFrame);
      return;
    }

    try {
      const probe = document.createElement("video");
      const onMeta = () => {
        probe.removeEventListener("loadedmetadata", onMeta);
        persistEndFrame(probe.duration);
      };
      probe.addEventListener("loadedmetadata", onMeta);
      probe.addEventListener("error", () => probe.removeEventListener("loadedmetadata", onMeta));
      probe.preload = "metadata";
      probe.src = url;
    } catch (e) {
      /* ignore */
    }
  };
}

export function selectBox(id, box) {
  return { type: SELECT_BOX, payload: { id, box } };
}

export function selectBoxAll(id, box) {
  return { type: SELECT_BOX_ALL, payload: { id, box } };
}

export const SELECT_BOX_TOGGLE = "SELECT_BOX_TOGGLE";

export function selectBoxToggle(id, current) {
  // `current` = currently single-selected box, folded into the multi-selection.
  return { type: SELECT_BOX_TOGGLE, payload: { id, current } };
}

export function moveBox(id, x, y, position, parent, container) {
  return { type: MOVE_BOX, payload: { id, x, y, position, parent, container } };
}

export function changeAnimationDurations(id, firstduration, lastduration) {
  return { type: CHANGE_ANIMATION_DURATIONS, payload: { id, firstduration, lastduration } };
}

export function changeAnimationPositionNumber(id, positionNumber, parent, container) {
  return {
    type: CHANGE_ANIMATION_POSITION_NUMBER,
    payload: { id, positionNumber, parent, container },
  };
}

export function changeShowAnimation(id, showAnimation, parent, container) {
  return { type: CHANGE_SHOW_ANIMATION, payload: { id, showAnimation, parent, container } };
}

export function changeStartAnimation(id, animation, parent, container) {
  return { type: CHANGE_START_ANIMATION, payload: { id, animation, parent, container } };
}

export function changeMiddleAnimation(id, animation, parent, container) {
  return { type: CHANGE_MIDDLE_ANIMATION, payload: { id, animation, parent, container } };
}

export function changeEndAnimation(id, animation, parent, container) {
  return { type: CHANGE_END_ANIMATION, payload: { id, animation, parent, container } };
}

export function changeFinalAnimation(id, animation, parent, container) {
  return { type: CHANGE_FINAL_ANIMATION, payload: { id, animation, parent, container } };
}

export function resizeBox(id, structure) {
  return { type: RESIZE_BOX, payload: { id, structure } };
}
export function rotateBox(id, structure) {
  return { type: ROTATE_BOX, payload: { id, structure } };
}

export function updateBox(id, content, toolbar, state) {
  return { type: UPDATE_BOX, payload: { id, content, toolbar, state } };
}

export function updateBoxTimeline(id, timeline) {
  return { type: UPDATE_BOX_TIMELINE, payload: { id, timeline } };
}

// patches: { [boxId]: { startFrame, endFrame, ... } } — each one shallow-merged
// into that box's timeline exactly like updateBoxTimeline does.
export function repairMediaTimelines(patches) {
  return { type: REPAIR_MEDIA_TIMELINES, payload: { patches } };
}

// Interactive Video MVP — array CRUD over box.timeline.interactions.
// op = "add" | "update" | "remove" | "replace".
//   add:     payload.interaction is appended
//   update:  payload.interactionId + payload.patch shallow-merged into the
//            matching interaction
//   remove:  payload.interactionId is filtered out
//   replace: payload.interactions overwrites the whole array (bulk reorder /
//            generator output / template instantiation)
export function updateVideoInteractions(id, op, payload = {}) {
  return { type: UPDATE_VIDEO_INTERACTIONS, payload: { id, op, ...payload } };
}

// Authoring convenience — append a single interaction at a given time.
// Wraps updateVideoInteractions(op="add") so the TrackClip "+ Add
// interaction at this time" button doesn't need to know the op string,
// and reducer tests can target a single named factory. Mints a stable-
// looking id (callers may override by passing one in `extra.id`).
export function addVideoInteraction(boxId, startTime, extra = {}) {
  const id = extra.id || "iv-" + Date.now().toString(36);
  const interaction = {
    id,
    startTime: typeof startTime === "number" ? startTime : 0,
    endTime: typeof extra.endTime === "number" ? extra.endTime : null,
    kind: extra.kind || "VideoOverlay",
    params: extra.params || {},
    position: extra.position || { x: 10, y: 10, w: 60, h: 30 },
  };
  return updateVideoInteractions(boxId, "add", { interaction });
}

// === Interactive Video workflow action kinds ===
// These do not flow through Redux directly — they are action descriptors
// consumed by workflowActionExecutor (and the runner in
// InteractivePluginRenderer). Factories live here so the action shape is
// declared in one place and reusable by Architect / Suggestion-Apply.
export const VIDEO_ACTION_KIND_SEEK = "seekVideo";
export const VIDEO_ACTION_KIND_PAUSE = "pauseVideo";
export const VIDEO_ACTION_KIND_RESUME = "resumeVideo";

export function seekVideoAction(boxId, time) {
  return { kind: VIDEO_ACTION_KIND_SEEK, boxId, time };
}
export function pauseVideoAction(boxId) {
  return { kind: VIDEO_ACTION_KIND_PAUSE, boxId };
}
export function resumeVideoAction(boxId) {
  return { kind: VIDEO_ACTION_KIND_RESUME, boxId };
}

// === Interactive Video workflow trigger kinds ===
// onVideoTimeReached  — Visor runtime fires when video playback time
//                       crosses startTime (and again at endTime to dismiss).
// onVideoOverlayDismissed — fires after the learner finishes / closes the
//                           VideoOverlay item that onVideoTimeReached opened.
export const VIDEO_TRIGGER_ON_TIME_REACHED = "onVideoTimeReached";
export const VIDEO_TRIGGER_ON_OVERLAY_DISMISSED = "onVideoOverlayDismissed";

// If deleting/mutating scene content removes the element a mandatory rule was
// built on, turn that mandatory off. One helper, all types.
export function reconcileMandatoryForScene(sceneId) {
  return (dispatch, getState) => {
    if (!sceneId) return;
    const state = getState();
    const present = state && state.undoGroup && state.undoGroup.present;
    if (!present) return;
    if (mandatoryDependencyMissing(present, sceneId)) {
      dispatch(updateViewToolbar(sceneId, { ...MANDATORY_RESET }));
    }
  };
}

// Resolve a box's owning scene, then reconcile that scene's mandatory.
export function reconcileMandatoryForBox(boxId) {
  return (dispatch, getState) => {
    const state = getState();
    const present = state && state.undoGroup && state.undoGroup.present;
    const box = present && present.boxesById && present.boxesById[boxId];
    if (box && box.parent) dispatch(reconcileMandatoryForScene(box.parent));
  };
}

export function deleteBox(id, parent, container, children, cvs, page) {
  return (dispatch, getState) => {
    // Capture the owning scene before the box is gone (page arg, else parent).
    let sceneId = page || parent;
    if (!sceneId) {
      const present = getState() && getState().undoGroup && getState().undoGroup.present;
      const box = present && present.boxesById && present.boxesById[id];
      sceneId = box && box.parent;
    }
    dispatch({ type: DELETE_BOX, payload: { id, parent, container, children, cvs, page } });
    dispatch(reconcileMandatoryForScene(sceneId));
  };
}

export function setGuideTag(tag, value) {
  return { type: SET_GUIDE_TAG, payload: { tag, value } };
}

export function clearGuideTags() {
  return { type: CLEAR_GUIDE_TAGS, payload: {} };
}

export function migrateLegacyGuides() {
  return (dispatch, getState) => {
    const state = getState();
    const present = state && state.undoGroup && state.undoGroup.present;
    if (!present) return;
    dispatch({
      type: MIGRATE_LEGACY_GUIDES,
      payload: {
        boxesById: present.boxesById || {},
        pluginToolbarsById: present.pluginToolbarsById || {},
        navItemsById: present.navItemsById || {},
        containedViewsById: present.containedViewsById || {},
      },
    });
  };
}

// Sound-list AudioCues (deprecated) become single-file cues on the timeline.
export function migrateLegacySoundLists() {
  return (dispatch, getState) => {
    const present = getState()?.undoGroup?.present;
    if (!present) return;
    dispatch({
      type: MIGRATE_LEGACY_SOUND_LISTS,
      payload: {
        boxesById: present.boxesById || {},
        pluginToolbarsById: present.pluginToolbarsById || {},
        navItemsById: present.navItemsById || {},
        containedViewsById: present.containedViewsById || {},
        animationsById: present.animationsById || {},
      },
    });
  };
}

export function reorderSortableContainer(ids, parent) {
  return { type: REORDER_SORTABLE_CONTAINER, payload: { ids, parent } };
}

export function dropBox(id, row, col, parent, container, oldParent, oldContainer, position, index) {
  return {
    type: DROP_BOX,
    payload: { id, row, col, parent, container, oldParent, oldContainer, position, index },
  };
}

export function verticallyAlignBox(id, verticalAlign) {
  return { type: VERTICALLY_ALIGN_BOX, payload: { id, verticalAlign } };
}

export function increaseBoxLevel() {
  return { type: INCREASE_LEVEL, payload: {} };
}

export function changeBoxLayer(id, parent, container, value, boxes_array) {
  return { type: CHANGE_BOX_LAYER, payload: { id, parent, container, value, boxes_array } };
}

export function resizeSortableContainer(id, parent, height) {
  return { type: RESIZE_SORTABLE_CONTAINER, payload: { id, parent, height } };
}

export function deleteSortableContainer(id, parent, children, cvs, page) {
  return { type: DELETE_SORTABLE_CONTAINER, payload: { id, parent, children, cvs, page } };
}

export function changeSortableProps(id, parent, prop, value) {
  return { type: CHANGE_SORTABLE_PROPS, payload: { id, parent, prop, value } };
}
export function changeCols(id, parent, distribution, boxesAffected) {
  return { type: CHANGE_COLS, payload: { id, parent, distribution, boxesAffected } };
}

export function changeRows(id, parent, column, distribution, boxesAffected) {
  return { type: CHANGE_ROWS, payload: { id, parent, column, distribution, boxesAffected } };
}

export function reorderBoxes(parent, container, order) {
  return { type: REORDER_BOXES, payload: { parent, container, order } };
}

export function addRichMark(mark, view, toolbar) {
  return { type: ADD_RICH_MARK, payload: { mark, view, toolbar } };
}

export function moveRichMark(mark, value) {
  return { type: MOVE_RICH_MARK, payload: { mark, value } };
}

export function editRichMark(mark, view, toolbar) {
  return { type: EDIT_RICH_MARK, payload: { mark, view, toolbar } };
}

export function deleteRichMark(mark) {
  return { type: DELETE_RICH_MARK, payload: { mark } };
}

export function selectContainedView(id) {
  return { type: SELECT_CONTAINED_VIEW, payload: { id } };
}

export function toggleTextEditor(id, value, text, content) {
  return { type: TOGGLE_TEXT_EDITOR, payload: { id, value, text, content } };
}

// soundList: the copy's sounds, already re-id'd (withFreshSoundIds).
export function duplicateNavItem(id, newId, boxes, suffix, linkedCvs, soundList) {
  return {
    type: DUPLICATE_NAV_ITEM,
    payload: { id, newId, boxes, suffix, linkedCvs, soundList },
  };
}

export function duplicateNavItemByPayload(
  newId,
  boxes,
  suffix,
  exercises,
  navItems,
  viewToolbar,
  soundList,
  animationList
) {
  return {
    type: DUPLICATE_NAV_ITEM_BY_PAYLOAD,
    payload: { newId, boxes, suffix, exercises, navItems, viewToolbar, soundList, animationList },
  };
}

export function pasteBox(ids, box, toolbar, children, index, marks, score) {
  return { type: PASTE_BOX, payload: { ids, box, toolbar, children, index, marks, score } };
}

/**
 * pasteScene(rekeyed, { navItemSelected })
 *
 * Inserts a re-keyed scene into the store immediately after the currently-
 * selected scene. `rekeyed` is the output of rekeyScene() — all ids are
 * already fresh. Dispatches a single PASTE_SCENE action; each slice reducer
 * handles the data it owns.
 *
 * @param {Object} rekeyed  - { navItem, boxes, toolbars, marks, viewToolbar,
 *                             exercises, animations, soundList,
 *                             containedViews, newSceneId }
 * @param {Object} opts     - { navItemSelected } — the scene after which to insert
 */
export function pasteScene(rekeyed, { navItemSelected } = {}) {
  return (dispatch, getState) => {
    const state = getState();
    const present = state.undoGroup.present;
    const navItemsIds = present.navItemsIds;

    const selectedId = navItemSelected != null ? navItemSelected : state.navItemSelected;

    const position = navItemsIds.indexOf(selectedId) + 1;

    dispatch({
      type: PASTE_SCENE,
      payload: {
        newSceneId: rekeyed.newSceneId,
        navItem: rekeyed.navItem,
        boxes: rekeyed.boxes || {},
        toolbars: rekeyed.toolbars || {},
        marks: rekeyed.marks || {},
        viewToolbar: rekeyed.viewToolbar || {},
        exercises: rekeyed.exercises || null,
        animations: rekeyed.animations || {},
        soundList: rekeyed.soundList || [],
        containedViews: rekeyed.containedViews || {},
        navItemSelected: selectedId,
        position,
      },
    });
  };
}

/* export function changeDisplayMode(mode) {
    return { type: CHANGE_DISPLAY_MODE, payload: { mode } };
}
*/
export function setBusy(value, msg, reason = null) {
  return { type: SET_BUSY, payload: { value, msg, reason } };
}

export function changeGlobalConfig(prop, value) {
  return (dispatch, getState) => {
    dispatch({ type: CHANGE_GLOBAL_CONFIG, payload: { prop, value } });

    if (prop === "STATE" && value.requiredCorrectAnswers > 0) {
      const state = getState();
      const pluginToolbars = state.undoGroup.present.pluginToolbarsById;
      const exercises = state.undoGroup.present.exercises;

      Object.keys(exercises).forEach((pageId) => {
        const pageExercises = exercises[pageId].exercises || {};
        Object.keys(pageExercises).forEach((exerciseId) => {
          const plugin = pluginToolbars[exerciseId];
          if (
            plugin &&
            plugin.state &&
            plugin.state.buttons &&
            plugin.state.buttons !== undefined
          ) {
            dispatch(updatePluginToolbar(exerciseId, "main", "state", "buttons", "seeAnswer"));
          }
        });
      });
    }
  };
}

export function importState(state) {
  return { type: IMPORT_STATE, payload: state };
}

export function updatePluginToolbar(id, tab, accordion, name, value, deletedBoxes, page) {
  return {
    type: UPDATE_PLUGIN_TOOLBAR,
    payload: { id, tab, accordion, name, value, deletedBoxes, page },
  };
}

export function updateViewToolbar(id, elements) {
  return { type: UPDATE_VIEW_TOOLBAR, payload: { id, ...elements } };
}

export function applyImageUrlMap(urlMap) {
  return { type: APPLY_IMAGE_URL_MAP, payload: { urlMap } };
}

export function configScore(id, button, value, page) {
  return { type: CONFIG_SCORE, payload: { id, button, value, page } };
}

export function uploadFile(id, url, name, keywords, mimetype) {
  return { type: UPLOAD_FILE, payload: { id, url, name, keywords, mimetype } };
}

export function deleteFile(id) {
  return { type: DELETE_FILE, payload: { id } };
}

export function setCorrectAnswer(
  id,
  correctAnswer,
  page,
  isDnd = false,
  isFib = false,
  fibIndex = -1,
  dndIndex = -1
) {
  return {
    type: SET_CORRECT_ANSWER,
    payload: { id, correctAnswer, page, isDnd, isFib, fibIndex, dndIndex },
  };
}

export function importEdi(state) {
  return { type: IMPORT_EDI, payload: { state } };
}

export function uploadTemplate(
  id,
  name,
  boxes,
  exercises,
  navItems,
  viewToolbar,
  soundList,
  animationList
) {
  return {
    type: UPLOAD_TEMPLATE,
    payload: { id, name, boxes, exercises, navItems, viewToolbar, soundList, animationList },
  };
}

// Every sound carries a stable unique id. EDIT_SOUND re-sorts the list by
// startTime, so array indexes shuffle on every edit — id is the only identity
// that survives. Legacy entries without an id are migrated on their first edit.
function ensureSoundId(value) {
  if (!value || value.id) return value;
  return { ...value, id: newSoundId() };
}

// A sound whose endTime lands past the scene's authored duration (e.g. a
// regenerated narration that came out longer than the original) would
// otherwise get silently clamped by buildSceneTimeline, which treats
// navItem.duration as authoritative once set. Grow the scene to fit instead —
// mirrors addBoxThunk's video handling, which persists endFrame from media
// duration rather than dropping the overflow.
function extendNavItemDurationForSound(dispatch, getState, id, value) {
  const endSec = parseTimeStr(value && value.endTime);
  if (!(endSec > 0)) return;
  const present = (getState() && getState().undoGroup && getState().undoGroup.present) || {};
  const nav =
    (present.navItemsById && present.navItemsById[id]) ||
    (present.containedViewsById && present.containedViewsById[id]);
  const durF = nav && typeof nav.duration === "number" ? nav.duration : 0;
  if (durF <= 0) return; // Not authored yet — bootstrap auto-extend handles this case.
  const endFrame = Math.ceil(endSec * FPS);
  if (endFrame > durF) {
    dispatch(changeNavItemDuration(id, endFrame));
  }
}

export const NARRATION_TRACK_ID = "type:narration";
const NARRATION_FALLBACK_LEN_SEC = 5;

function soundSpanSec(sound) {
  const start = parseTimeStr(sound && sound.startTime) || 0;
  const explicitEnd = parseTimeStr(sound && sound.endTime) || 0;
  const dur = parseFloat(sound && sound.duration) || 0;
  const known = explicitEnd > start || dur > 0;
  const end =
    explicitEnd > start ? explicitEnd : start + (dur > 0 ? dur : NARRATION_FALLBACK_LEN_SEC);
  return { start, end, known };
}

export function addSound(id, value) {
  return (dispatch, getState) => {
    // Insert-at-playhead: an unpositioned new sound (startTime absent or 0)
    // shifts to the playhead. Explicitly positioned sounds (split pieces,
    // user-typed times) and sounds for a non-displayed scene pass untouched.
    const startSec = parseTimeStr(value && value.startTime) || 0;
    if (startSec === 0) {
      const playhead = getEditorPlayhead();
      if (playhead && playhead.frame > 0 && playhead.sceneId === id) {
        const shiftSec = playhead.frame / FPS;
        const endSec = parseTimeStr(value && value.endTime) || 0;
        value = {
          ...value,
          startTime: shiftSec,
          ...(endSec > 0 ? { endTime: endSec + shiftSec } : {}),
        };
      }
    }
    // Verify incoming narration rows as duplicate/regenerate flows may reuse trackId.
    if (value && value.trackId !== null && !value.loopScene) {
      const present = (getState() && getState().undoGroup && getState().undoGroup.present) || {};
      const sceneSounds = (present.soundList && present.soundList[id]) || [];
      const span = soundSpanSec(value);
      if (typeof value.trackId === "string") {
        // An explicit row (split piece, duplicate) is only honoured when it
        // still has room — never adopted into a different one.
        const occupied =
          !span.known ||
          sceneSounds.some((s) => {
            if (!s || s.trackId !== value.trackId) return false;
            const m = soundSpanSec(s);
            return !m.known || (m.start < span.end && m.end > span.start);
          });
        if (occupied) {
          const { trackId, ...withoutTrack } = value; // eslint-disable-line no-unused-vars
          value = withoutTrack;
        }
      } else {
        // Same policy the boxes use: scan every narration row, adopting an
        // ungrouped sound (legacy scene, or one that once had to open its own
        // row) so the new one lands NEXT to it instead of on a row of its own.
        const rows = [];
        const byTrack = new Map();
        sceneSounds.forEach((sound, index) => {
          if (!sound || sound.loopScene || sound.trackId === null) return;
          const m = soundSpanSec(sound);
          const key = sound.id ?? index;
          if (typeof sound.trackId === "string") {
            let row = byTrack.get(sound.trackId);
            if (!row) {
              row = { trackId: sound.trackId, memberIds: [], members: [], sounds: [] };
              byTrack.set(sound.trackId, row);
              rows.push(row);
            }
            row.memberIds.push(key);
            row.members.push(m);
            row.sounds.push(sound);
          } else {
            rows.push({ trackId: null, memberIds: [key], members: [m], sounds: [sound] });
          }
        });
        // An unknown length can never be fitted between neighbours: it spans
        // everything, so only a row that does not exist yet can take it.
        const placement = pickRowForSpan(
          rows,
          NARRATION_TRACK_ID,
          span.known ? span.start : -Infinity,
          span.known ? span.end : Infinity
        );
        if (placement.trackId) value = { ...value, trackId: placement.trackId };
        placement.adoptIds.forEach((key, i) =>
          dispatch(editSound(id, key, { ...placement.row.sounds[i], trackId: placement.trackId }))
        );
        // Rows keep their vertical slot: a sound joining one takes that row's
        // display position instead of the "newest goes to the bottom" default.
        const joinedOrders = ((placement.row && placement.row.sounds) || [])
          .map((s) => s.rowOrder)
          .filter((r) => typeof r === "number");
        if (joinedOrders.length) value = { ...value, rowOrder: Math.min(...joinedOrders) };
      }
    }
    // Persist display order independently from chronological soundList order.
    if (value && typeof value.rowOrder !== "number") {
      const present = (getState() && getState().undoGroup && getState().undoGroup.present) || {};
      const sceneSounds = (present.soundList && present.soundList[id]) || [];
      const numeric = sceneSounds.map((s) => s && s.rowOrder).filter((r) => typeof r === "number");
      // -1 floor keeps it under the legacy fallback band (≈ base − 1 − idx/1e6).
      const min = numeric.length ? Math.min(...numeric) : -1;
      value = { ...value, rowOrder: Math.min(-1, min) - 1 };
    }
    dispatch({ type: ADD_SOUND, payload: { id, value: ensureSoundId(value) } });
    extendNavItemDurationForSound(dispatch, getState, id, value);
  };
}

// `key` is the sound's id (string); a numeric key is the legacy index fallback.
export function editSound(id, key, value) {
  return (dispatch, getState) => {
    dispatch({ type: EDIT_SOUND, payload: { id, key, value: ensureSoundId(value) } });
    extendNavItemDurationForSound(dispatch, getState, id, value);
  };
}

// Purely a Timeline row-display position for one or more scene-audio
// (narration) layers — independent of the array's own storage order, which
// EDIT_SOUND/ADD_SOUND always keep sorted chronologically by startTime
// (other flows, e.g. AvatarNarrationTab's soundList[0], rely on that staying
// chronological). `rowOrder` shares the same numeric axis as regular layers'
// zIndex (fractional — e.g. 2.5 slots the row between boxes at zIndex 2 and
// 3), which is what lets a narration row be dragged in among any other
// element, not just other narration rows — see buildLayers.js's sceneAudioPos.
// Batched (`updates: [{ soundId, rowOrder }, ...]`) because dragging ANY row
// — a box past a narration, or vice versa — can shift more than one
// narration's effective position at once; batching keeps that one drag a
// single undo step.
export function setSceneAudioRowOrders(sceneId, updates) {
  return { type: SET_SCENE_AUDIO_ROW_ORDERS, payload: { sceneId, updates } };
}

export function deleteSound(id, key) {
  return (dispatch) => {
    dispatch({ type: DELETE_SOUND, payload: { id, key } });
    // Removing the scene's last narration invalidates a "waitForSound" mandatory.
    dispatch(reconcileMandatoryForScene(id));
  };
}

export function deleteTemplate(id) {
  return { type: DELETE_TEMPLATE, payload: { id } };
}

export function addAnimation(id, animation) {
  return { type: ADD_ANIMATION, payload: { id, animation } };
}

export function saveAnimation(id, animations) {
  return { type: SAVE_ANIMATION, payload: { id, animations } };
}

export function deleteAnimation(id) {
  return (dispatch) => {
    dispatch({ type: DELETE_ANIMATION, payload: { id } });
    // Removing a box's animation can invalidate a "waitForAnimation" mandatory.
    dispatch(reconcileMandatoryForBox(id));
  };
}

export function updateTextAnimation(id, animation, duration, delay = 0) {
  return { type: UPDATE_TEXT_ANIMATION, payload: { id, animation, duration, delay } };
}

export function updateBoxAnimation(id, animation, duration, delay = 0) {
  return { type: UPDATE_BOX_ANIMATION, payload: { id, animation, duration, delay } };
}

export function updateBoxExitAnimation(id, animation, duration) {
  return { type: UPDATE_BOX_EXIT_ANIMATION, payload: { id, animation, duration } };
}

export function deleteBoxExitAnimation(id) {
  return { type: DELETE_BOX_EXIT_ANIMATION, payload: { id } };
}

export function deleteTextAnimation(id) {
  return (dispatch) => {
    dispatch({ type: DELETE_TEXT_ANIMATION, payload: { id } });
    dispatch(reconcileMandatoryForBox(id));
  };
}

export function deleteBoxAnimation(id) {
  return (dispatch) => {
    dispatch({ type: DELETE_BOX_ANIMATION, payload: { id } });
    dispatch(reconcileMandatoryForBox(id));
  };
}

export function deleteRemoteFileVishAsync(id, url, callback) {
  return (dispatch) => {
    dispatch(setBusy(true, FILE_DELETING));

    let form = new FormData();
    form.append("_method", "delete");
    if (typeof idealStudio_editor_params !== "undefined") {
      form.append("authenticity_token", idealStudio_editor_params.authenticity_token);
    }

    dispatch(setBusy(false, id));

    dispatch(deleteFile(id));
    if (callback) {
      callback(true);
    }
    return true;

    return fetch(url, {
      method: "POST",
      credentials: "same-origin",
      body: form,
    })
      .then((response) => {
        if (!response.ok) {
          if (response.status === 406) {
            return 200;
          }
          throw Error(response.statusText);
        }

        return 200;
      })
      .then((result) => {
        dispatch(setBusy(false, id));

        dispatch(deleteFile(id));
        if (callback) {
          callback(result);
        }
      })
      .catch((e) => {
        dispatch(setBusy(false, FILE_DELETE_ERROR));
        if (callback) {
          callback();
        }
        return false;
      });
  };
}

// Async actions
export function exportStateAsync(
  state,
  win = null,
  url = null,
  id = null,
  tenantId = null,
  force = false
) {
  return (dispatch) => {
    let exportedState = {
      present: {
        ...state.undoGroup.present,
        filesUploaded: state.filesUploaded,
        status: state.status,
        everPublished: state.everPublished,
      },
    };
    // First dispatch: the app state is updated to inform
    // that the API call is starting.
    dispatch(setBusy(true, i18n.t("messages.operation_in_progress"), "saving_state"));

    // The function called by the thunk middleware can return a value,
    // that is passed on as the return value of the dispatch method.

    // In this case, we return a promise to wait for.
    // This is not required by thunk middleware, but it is convenient for us.

    let token = getToken();

    if (
      process.env.NODE_ENV !== "production" || idealStudio_editor_params
        ? idealStudio_editor_params === undefined
        : null
    ) {
      const queryParams = new URLSearchParams(window?.location?.search);
      const isTemplateView = queryParams?.get("template") === "true";

      return apiPut(
        IdealStudio.Config.export_url,
        {
          id: id,
          exportedState: JSON.stringify(exportedState),
          isTemplate: isTemplateView,
          ...buildBaseUpdatedAtField(force),
        },
        {
          headers: {
            Accept: "application/json",
            "Abp.TenantId": tenantId,
            Authorization: "Bearer " + token,
          },
        }
      )
        .then((response) =>
          response
            .json()
            .catch(() => null)
            .then((body) => ({ status: response.status, body }))
        )
        .then(({ status, body }) => {
          const conflict = parseSaveConflict(status, body);
          if (conflict) {
            dispatch(setBusy(false, i18n.t("error.exporting")));
            return { conflict: true, currentUpdatedAt: conflict.currentUpdatedAt };
          }
          if (status >= 400 || !body) {
            throw new Error(i18n.t("error.exporting"));
          }
          updateBaseUpdatedAtFromResponse(body);
          deleteDraft(id || window.idealStudioTrainingId, getUserId());
          dispatch(setBusy(false, i18n.t("success_transaction"), "saving_state"));
          return { success: true };
        })
        .catch((e) => {
          saveDraftFromState(id || window.idealStudioTrainingId, state, getUserId());
          dispatch(setBusy(false, i18n.t("error.exporting")));
          return { success: false };
        });
    }

    let data = {
      authenticity_token: idealStudio_editor_params.authenticity_token,
      idealStudio_document: {
        user: { name: idealStudio_editor_params.name, id: idealStudio_editor_params.id },
        json: exportedState,
      },
    };

    const queryParams = new URLSearchParams(window?.location?.search);
    const isTemplateView = queryParams?.get("template") === "true";

    return apiPut(
      idealStudio_editor_params.export_url,
      {
        id: id,
        exportedState: JSON.stringify(exportedState),
        isTemplate: isTemplateView,
        ...buildBaseUpdatedAtField(force),
      },
      {
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
          "Abp.TenantId": tenantId,
          Authorization: "Bearer " + token,
        },
      }
    )
      .then((response) =>
        response
          .json()
          .catch(() => null)
          .then((body) => ({ status: response.status, body }))
      )
      .then(({ status, body }) => {
        const conflict = parseSaveConflict(status, body);
        if (conflict) {
          dispatch(setBusy(false, i18n.t("error.exporting")));
          return { conflict: true, currentUpdatedAt: conflict.currentUpdatedAt };
        }
        if (status >= 400 || !body) {
          throw new Error(i18n.t("error.exporting"));
        }

        updateBaseUpdatedAtFromResponse(body);
        deleteDraft(id || window.idealStudioTrainingId, getUserId());

        let idealStudio_resource_id = body && body.idealStudio_id;

        if (IdealStudio.Config.api_editor_url_change) {
          window.parent.history.replaceState(
            "",
            "",
            IdealStudio.Config.export_url +
              idealStudio_resource_id +
              idealStudio_editor_params.edit_prefix
          );
          idealStudio_editor_params.export_url =
            IdealStudio.Config.export_url + idealStudio_resource_id;
          idealStudio_editor_params.idealStudio_resource_id = idealStudio_resource_id;
        }
        if (win !== null) {
          win.parent.location.href = url || idealStudio_editor_params.export_url;
          win.focus();
        }
        dispatch(setBusy(false, i18n.t("success_transaction"), "saving_state"));
        return { success: true };
      })
      .catch((e) => {
        saveDraftFromState(id || window.idealStudioTrainingId, state, getUserId());
        dispatch(setBusy(false, i18n.t("error.exporting")));
        return { success: false };
      });
  };
}

export function importStateAsync(id = null, tenantId = null, callback = null) {
  return (dispatch) => {
    dispatch(setBusy(true, i18n.t("Importing")));

    let loading = document.getElementById("_loading");
    if (loading) loading.style.display = "block";

    let overlay = document.getElementById("_overlay");
    if (overlay) overlay.style.display = "block";

    // Dev modda local state kullan
    /* if (process.env.NODE_ENV === "development") {
      try {
        const devState = require("../dev_state.js").default;

        if (devState && devState.exportedState) {
          let exportedState = devState.exportedState;

          // exportedState string ise parse et, değilse direkt kullan
          let parsedState =
            typeof exportedState === "string" ? JSON.parse(exportedState) : exportedState;

          if (callback) {
            callback({ baseUrl: devState.baseUrl || "" });
          }

          dispatch(importState(serialize(parsedState)));

          // Hide loading elements
          if (loading) loading.style.display = "none";
          if (overlay) overlay.style.display = "none";

          dispatch(setBusy(false, i18n.t("success_transaction")));
          return Promise.resolve(true);
        }
      } catch (error) {
        console.warn("Dev state yüklenemedi, servisten yüklenecek:", error);
        // Devam et, servisten yükle
      }
    } */

    let token = getToken();

    const headers = {
      Accept: "application/json",
    };

    if (token) {
      headers.Authorization = "Bearer " + token;
    }

    if (tenantId) {
      headers["Abp.TenantId"] = tenantId;
    }

    const queryParams = new URLSearchParams(window?.location?.search);
    const isTemplateView = queryParams?.get("template") === "true";
    return apiGet(
      window.ideaLibraryRootUrl +
        IdealStudio.Config.import_url +
        id +
        "?isTemplate=" +
        isTemplateView,
      {
        headers: headers,
      }
    )
      .then((response) => {
        if (response.status >= 400) {
          throw new Error(i18n.t("error.importing"));
        }
        return response.text();
      })
      .then((result) => {
        let responseData = JSON.parse(result);

        if (!responseData) {
          throw new Error("Invalid response structure: missing data");
        }

        // Save or remove imagePromptLibraryId from localStorage
        if (responseData.aiTrainings) {
          if (responseData.aiTrainings.imagePromptLibraryId) {
            localStorage.setItem(
              "imagePromptLibraryId",
              responseData.aiTrainings.imagePromptLibraryId
            );
          } else {
            localStorage.removeItem("imagePromptLibraryId");
          }
        }

        if (callback) {
          callback(responseData);
        }

        let exportedState = responseData.exportedState;

        if (!exportedState) {
          throw new Error("Invalid response: exportedState is missing or null");
        }

        dispatch(importState(serialize(JSON.parse(exportedState))));

        dispatch(ActionCreators.clearHistory());

        // Hide loading elements after state is imported
        let loading = document.getElementById("_loading");
        if (loading) loading.style.display = "none";

        let overlay = document.getElementById("_overlay");
        if (overlay) overlay.style.display = "none";

        return true;
      })
      .then(() => {
        dispatch(setBusy(false, i18n.t("success_transaction")));
        markEditorLoaded();
      })
      .catch((e) => {
        console.error("importStateAsync - Error occurred:", e);
        console.error("importStateAsync - Error stack:", e.stack);

        if (!isEditorLoaded()) {
          // First-load failure of any status → blocking gate. A first-load
          // 401/403 already fired the auth-flavored popup from apiFetch and
          // latched it, so this serverError call is a no-op in that case and
          // only takes effect for 404/500/network — exactly the flavor split
          // we want, no status inspection.
          triggerAuthRequired("serverError");
        } else if (!isAuthRequiredTriggered() && window.showServerModal) {
          // Runtime reload failure → non-blocking error message, never the
          // auth popup.
          window.showServerModal(
            i18n.t("authRequired.runtimeErrorTitle", { defaultValue: "Connection problem" }),
            i18n.t("authRequired.runtimeErrorDescription", {
              defaultValue:
                "Couldn't reach the server. Please check your connection and try again.",
            }),
            false
          );
        }

        let loading = document.getElementById("_loading");
        if (loading) loading.style.display = "none";

        let overlay = document.getElementById("_overlay");
        if (overlay) overlay.style.display = "none";

        let errorMessage = i18n.t("error.importing");
        if (e && e.message) {
          errorMessage = errorMessage + ": " + e.message;
          console.error("importStateAsync - Error message:", e.message);
        }

        dispatch(setBusy(false, errorMessage));
      });
  };
}

export function importStateAsyncSimulate(id = null, tenantId = null, callback = null) {
  return (dispatch) => {
    dispatch(setBusy(true, i18n.t("Importing")));
    let loading = document.getElementById("_loading");

    console.log(loading);
    if (loading) loading.style.display = "block";

    let overlay = document.getElementById("_overlay");

    console.log(overlay);
    if (overlay) overlay.style.display = "block";

    return fetch("./local.173.state.json", {
      method: "GET",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    })
      .then((response) => {
        return new Promise((resolve) => {
          setTimeout(() => {
            if (response.status >= 400) {
              throw new Error(i18n.t("error.importing"));
            }
            resolve(response.text());
          }, 3000);
        });
      })
      .then((result) => {
        let parsedResult = JSON.parse(result);
        let state = parsedResult.result.exportedState;

        if (callback) {
          callback(parsedResult.result.baseUrl || "");
        }
        dispatch(importState(serialize(JSON.parse(state))));

        let loading = document.getElementById("_loading");
        if (loading) loading.style.display = "none";

        let overlay = document.getElementById("_overlay");
        if (overlay) overlay.style.display = "none";

        return true;
      })
      .then(() => {
        dispatch(setBusy(false, i18n.t("success_transaction")));
      })
      .catch((e) => {
        let loading = document.getElementById("_loading");
        if (loading) loading.style.display = "none";

        let overlay = document.getElementById("_overlay");
        if (overlay) overlay.style.display = "none";

        dispatch(setBusy(false, i18n.t("error.importing")));
      });
  };
}

export function uploadVishResourceAsync(query, keywords = "", callback, percentageCallback) {
  return (dispatch) => {
    if (query && query.name && query.name.length > 0) {
      let filename = query.name;
      dispatch(setBusy(true, FILE_UPLOADING));

      let form = new FormData();
      form.append("document[title]", query.name);
      form.append("document[description]", "Uploaded using IdealStudio Editor");
      // form.append("document[tag_list][]", keywords);
      /*
            if (typeof(idealStudio_editor_params) !== 'undefined') {
                form.append("document[owner_id]", idealStudio_editor_params.id);
                form.append("document[scope]", "1");
                form.append("authenticity_token", idealStudio_editor_params.authenticity_token);
            } */
      form.append("document[file]", query);
      let filenameDeconstructed = filename.split(".");
      let mimetype =
        query.type && query.type !== ""
          ? query.type
          : filenameDeconstructed[filenameDeconstructed.length - 1];

      return _LoadMediaFile(
        query,
        (name, type, src) => {
          let id = ID_PREFIX_FILE + Date.now();
          mimetype = type === "scormpackage" || type === "webapp" ? type : mimetype;
          if (mimetype === "application/vnd.ms-excel") {
            mimetype = "csv";
          }
          dispatch(uploadFile(id, src, name, keywords, mimetype));
          if (callback) {
            callback(src);
          }
          dispatch(setBusy(false, id));
        },
        percentageCallback,
        (diagnostics) => {
          dispatch(setBusy(false, FILE_UPLOAD_ERROR));
          if (callback) {
            callback(undefined, diagnostics);
          }
        }
      );

      /*
            return fetch(IdealStudio.Config.upload_vish_url, {
                method: 'POST',
                //credentials: 'same-origin',
                body: JSON.stringify({ id : 1 }),
                headers: {
                  'Abp.TenantId' : 1,
                  'Content-Type': 'application/json'
                },
            }).then(response => {
                if (!response.ok) {
                    throw Error(response.statusText);
                }

                return response.text().then((text)=>{
                    return JSON.parse(text);
                });
            }).then((result) => {

                let id = ID_PREFIX_FILE + Date.now();
                mimetype = (result.type === 'scormpackage' || result.type === 'webapp') ? result.type : mimetype;
                if (mimetype === 'application/vnd.ms-excel') {
                    mimetype = 'csv';
                }
                dispatch(uploadFile(id, result.src, query.name, keywords, mimetype));
                if (callback) {
                    callback(result.src);
                }
                dispatch(setBusy(false, id));
            })
                .catch(e => {
                    dispatch(setBusy(false, FILE_UPLOAD_ERROR));
                    if (callback) {
                        callback();
                    }
                }); */

      // alert(i18n.t("error.file_extension_invalid"));
    }
    alert(i18n.t("error.file_not_selected"));

    return false;
  };
}

export function ttsGenerateSoundFile(query, callback) {
  return (dispatch) => {
    if (query && query.prompt && query.prompt.length > 0) {
      dispatch(setBusy(true, FILE_UPLOADING));

      return _ideaEnvTtsGetVoice(
        query,
        (isSuccess, errorStr, fileUrl, subscriptionUsed, subscriptionLimit) => {
          if (isSuccess === true && fileUrl && fileUrl !== "") {
            dispatch(uploadFile(query.id, fileUrl, query.fileName, null, "audio/mpeg"));
          }
          if (callback) {
            callback(isSuccess, errorStr, fileUrl, subscriptionUsed, subscriptionLimit);
          }
          dispatch(setBusy(false, query.id));
        }
      );
    }
    alert(i18n.t("error.file_not_selected"));

    return false;
  };
}

export function ttsGetSoundMetaFile(query, callback) {
  return (dispatch) => {
    if (query && query.soundfileurl && query.soundfileurl.length > 0) {
      // dispatch(setBusy(true, FILE_UPLOADING));

      // Check if the environment function exists
      if (typeof _ideaEnvTtsGetVoiceMeta === "function") {
        return _ideaEnvTtsGetVoiceMeta(query, (isSuccess, errorStr, _metaJson) => {
          if (callback) {
            callback(isSuccess, errorStr, _metaJson);
          }
        });
      } else {
        // Fallback when environment function is not available
        if (callback) {
          callback(false, "TTS meta function not available", null);
        }
        return false;
      }
    }
    if (callback) {
      callback(false, "No sound file URL provided", null);
    }
    return false;
  };
}

export function ttiGenerateImageFile(query, callback) {
  //{ prompt, aiType, imgSize, imgCount }

  return (dispatch) => {
    if (query && query.prompt && query.prompt.length > 0) {
      dispatch(setBusy(true, FILE_UPLOADING));

      return _ideaEnvTtiGetImage(
        query,
        (isSuccess, errorStr, fileUrl, subscriptionUsed, subscriptionLimit) => {
          if (isSuccess === true && fileUrl && fileUrl !== "") {
            dispatch(uploadFile(query.id, fileUrl, query.fileName, null, "image/png"));
          }
          if (callback) {
            callback(isSuccess, errorStr, fileUrl, subscriptionUsed, subscriptionLimit);
          }
          dispatch(setBusy(false, query.id));
        }
      );
    }
    alert(i18n.t("error.file_not_selected"));

    return false;
  };
}

// === Task 21 (workflow suggestions) ===
// Replaced by Interactivity Studio (#4): openWorkflowSuggestionsForCurrentScene
// now lives in the backward-compat wrappers block at the end of this file and
// dispatches openInteractivityStudio({ tab: "suggestions" }).
export const OPEN_WORKFLOW_SUGGESTIONS = "OPEN_WORKFLOW_SUGGESTIONS";

// === Task 24 Workflow slice action types ===
// Workflow data lives INSIDE navItemsById[sectionId].workflow (per spec §6.1).
// These action types are consumed by reducers/nav_items_by_id.es6.
// OPEN_FLOW_VIEWER / CLOSE_FLOW_VIEWER drive the non-undoable flowViewerOpen
// reducer added in reducers/flow_viewer.es6 (used by Task 22's header button).
export const SET_SECTION_WORKFLOW = "SET_SECTION_WORKFLOW";
export const ADD_WORKFLOW_TRIGGER = "ADD_WORKFLOW_TRIGGER";
export const UPDATE_WORKFLOW_TRIGGER = "UPDATE_WORKFLOW_TRIGGER";
export const DELETE_WORKFLOW_TRIGGER = "DELETE_WORKFLOW_TRIGGER";
export const ADD_WORKFLOW_VARIABLE = "ADD_WORKFLOW_VARIABLE";
export const UPDATE_WORKFLOW_VARIABLE = "UPDATE_WORKFLOW_VARIABLE";
export const DELETE_WORKFLOW_VARIABLE = "DELETE_WORKFLOW_VARIABLE";
export const MERGE_WORKFLOW_PROPOSAL = "MERGE_WORKFLOW_PROPOSAL";
export const SET_WORKFLOW_SCORING_MODE = "SET_WORKFLOW_SCORING_MODE";

export const OPEN_FLOW_VIEWER = "OPEN_FLOW_VIEWER";
export const CLOSE_FLOW_VIEWER = "CLOSE_FLOW_VIEWER";

export function setSectionWorkflow(sectionId, workflow) {
  return { type: SET_SECTION_WORKFLOW, payload: { sectionId, workflow } };
}
export function addWorkflowTrigger(sectionId, trigger) {
  return { type: ADD_WORKFLOW_TRIGGER, payload: { sectionId, trigger } };
}
export function updateWorkflowTrigger(sectionId, triggerId, patch) {
  return { type: UPDATE_WORKFLOW_TRIGGER, payload: { sectionId, triggerId, patch } };
}
export function deleteWorkflowTrigger(sectionId, triggerId) {
  return { type: DELETE_WORKFLOW_TRIGGER, payload: { sectionId, triggerId } };
}
export function addWorkflowVariable(sectionId, variable) {
  return { type: ADD_WORKFLOW_VARIABLE, payload: { sectionId, variable } };
}
export function updateWorkflowVariable(sectionId, varId, patch) {
  return { type: UPDATE_WORKFLOW_VARIABLE, payload: { sectionId, varId, patch } };
}
export function deleteWorkflowVariable(sectionId, varId) {
  return { type: DELETE_WORKFLOW_VARIABLE, payload: { sectionId, varId } };
}
export function mergeWorkflowProposal(sectionId, proposal) {
  // single undo step that inserts both triggers + variables atomically
  return { type: MERGE_WORKFLOW_PROPOSAL, payload: { sectionId, proposal } };
}
// Per-section scoring toggle. mode ∈ {"none","static","dynamic"}.
// When "dynamic", the workflow runtime accumulates a built-in `score` number
// variable from BranchingQuestion alternatives + EndScreen.terminalScore.
// "static" keeps EndScreen scoring but doesn't auto-create the var; "none"
// hides scoring chrome entirely.
export function setWorkflowScoringMode(sectionId, mode) {
  return { type: SET_WORKFLOW_SCORING_MODE, payload: { sectionId, mode } };
}
// Thunk: ensures a built-in `score` (type number, initial 0) variable exists
// when scoringMode === "dynamic". Idempotent — bails if the variable already
// exists. Used by ScoringModeToggle when flipping to dynamic.
export function ensureScoreVariable(sectionId) {
  return (dispatch, getState) => {
    if (sectionId === null || sectionId === undefined) return;
    const state = getState();
    const present = (state && state.undoGroup && state.undoGroup.present) || {};
    const navItemsById = present.navItemsById || {};
    const sec = navItemsById[sectionId];
    const wf = (sec && sec.workflow) || {};
    const variables = Array.isArray(wf.variables) ? wf.variables : [];
    if (variables.some((v) => v && v.id === "score")) return;
    dispatch(
      addWorkflowVariable(sectionId, {
        id: "score",
        type: "number",
        initial: 0,
        scope: "session",
        builtin: true,
      })
    );
  };
}
// openFlowViewer / closeFlowViewer are now defined in the backward-compat
// wrappers block at the end of this file (they dispatch openInteractivityStudio
// / closeInteractivityStudio instead of the old flow_viewer slice actions).

// === Task 23 === AKIŞ ÖĞELERİ panel: per-item editing on InteractivePlugin boxes.
// These action types mutate state[boxId].state.items inside the
// plugin_toolbars_by_id reducer (one new case each).
export const ADD_PLUGIN_TOOLBAR_ITEM = "ADD_PLUGIN_TOOLBAR_ITEM";
export const UPDATE_PLUGIN_TOOLBAR_ITEM = "UPDATE_PLUGIN_TOOLBAR_ITEM";
export const REMOVE_PLUGIN_TOOLBAR_ITEM = "REMOVE_PLUGIN_TOOLBAR_ITEM";

export function addPluginToolbarItem(boxId, item) {
  return { type: ADD_PLUGIN_TOOLBAR_ITEM, payload: { boxId, item } };
}
export function updatePluginToolbarItem(boxId, itemId, patch) {
  return { type: UPDATE_PLUGIN_TOOLBAR_ITEM, payload: { boxId, itemId, patch } };
}
export function removePluginToolbarItem(boxId, itemId) {
  return { type: REMOVE_PLUGIN_TOOLBAR_ITEM, payload: { boxId, itemId } };
}
// === /Task 23 ===

// === Task 25 Workflow overlay ===
// Drives the transient (non-undoable) workflowOverlayOpen reducer in
// reducers/workflow_overlay.es6. Consumed by the floating workflow FAB
// (WorkflowFAB.jsx) and the overlay component added in Task 27.
export const OPEN_WORKFLOW_OVERLAY = "OPEN_WORKFLOW_OVERLAY";
export const CLOSE_WORKFLOW_OVERLAY = "CLOSE_WORKFLOW_OVERLAY";
// openWorkflowOverlay / closeWorkflowOverlay are now defined in the
// backward-compat wrappers block at the end of this file (they dispatch
// openInteractivityStudio / closeInteractivityStudio).
// === /Task 25 ===

// === Task 37 Workflow suggestions loader ===
// Drives the transient `workflowSuggestions` reducer
// (reducers/workflow_suggestions.es6). Loads the active suggestion-list row
// for the current training/scene from the middleware endpoint added in the
// same task. The reducer is intentionally OUTSIDE undoGroup because
// suggestions are server-derived, transient UI state — undoing a click in the
// editor should not also rewind which suggestions are loaded.
export const LOAD_SUGGESTIONS_REQUEST = "LOAD_SUGGESTIONS_REQUEST";
export const LOAD_SUGGESTIONS_SUCCESS = "LOAD_SUGGESTIONS_SUCCESS";
export const LOAD_SUGGESTIONS_FAILURE = "LOAD_SUGGESTIONS_FAILURE";

export function loadSuggestionsThunk(trainingId, sceneId) {
  return async (dispatch) => {
    dispatch({
      type: LOAD_SUGGESTIONS_REQUEST,
      payload: { trainingId, sceneId },
    });
    try {
      // Lazy require avoids the circular dependency between common/actions
      // and common/server_utils (server_utils imports thunks indirectly via
      // http_service helpers in some call sites).
      const { loadWorkflowSuggestions } = require("./server_utils.es6");
      const data = await loadWorkflowSuggestions(trainingId, sceneId);
      dispatch({
        type: LOAD_SUGGESTIONS_SUCCESS,
        payload: data || { suggestions: null, navIds: [], generatedAt: null },
      });
    } catch (err) {
      dispatch({
        type: LOAD_SUGGESTIONS_FAILURE,
        payload: { error: String((err && err.message) || err) },
      });
    }
  };
}
// === /Task 37 ===

// === Task 39 Workflow generation enqueue thunk ===
// Thin async wrapper around server_utils.enqueueWorkflowGeneration. Returns
// the `{ jobId, room }` envelope back to the caller (WorkflowOverlay), which
// drives the GenerationModal subscription to `job_<jobId>`. We intentionally
// do not store the inflight job in Redux — the GenerationModal owns the
// transient UI for "active suggestion job"; the cross-app job widget
// independently tracks it via the per-user socket room (Phase 9 widget).
export const ENQUEUE_WORKFLOW_GENERATION_REQUEST = "ENQUEUE_WORKFLOW_GENERATION_REQUEST";
export const ENQUEUE_WORKFLOW_GENERATION_SUCCESS = "ENQUEUE_WORKFLOW_GENERATION_SUCCESS";
export const ENQUEUE_WORKFLOW_GENERATION_FAILURE = "ENQUEUE_WORKFLOW_GENERATION_FAILURE";

export function enqueueWorkflowGenerationThunk(trainingId, sectionId, suggestionId) {
  return async (dispatch) => {
    dispatch({
      type: ENQUEUE_WORKFLOW_GENERATION_REQUEST,
      payload: { trainingId, sectionId, suggestionId },
    });
    try {
      // Lazy require to keep parity with loadSuggestionsThunk (avoids the
      // circular dep between common/actions and common/server_utils).
      const { enqueueWorkflowGeneration } = require("./server_utils.es6");
      const data = await enqueueWorkflowGeneration(trainingId, sectionId, suggestionId);
      dispatch({
        type: ENQUEUE_WORKFLOW_GENERATION_SUCCESS,
        payload: data || null,
      });
      return data;
    } catch (err) {
      dispatch({
        type: ENQUEUE_WORKFLOW_GENERATION_FAILURE,
        payload: { error: String((err && err.message) || err) },
      });
      throw err;
    }
  };
}
// === /Task 39 ===

// === Round 27 — Apply suggestion (materialize as interactive items) ===
// Wires SuggestionCard.onPick (SuggestionsTab) to the InteractivePlugin
// canvas. For each `kind` in suggestion.shape.pluginKindHints we build an item
// using defaultParamsFor(kind) and dispatch ADD_INTERACTIVE_ITEM (auto-seeding
// an InteractivePlugin box if the active scene doesn't have one yet via
// seedInteractivePluginBoxAndAddItem). Each item carries
// `source: "ai"` + `suggestionId` for provenance so later UI (filters,
// "regenerate from this suggestion") can find items born from a given pick.
//
// If suggestion.shape.hasBranching is true AND no BranchingQuestion is in
// pluginKindHints we prepend one — the branching item is the only way the
// flow-viewer can render an alt-based branch, so a "branching" suggestion
// without it would visually misrepresent the suggestion.
//
// We also fire-and-forget the server-side enqueue thunk so the workflow-
// architect pipeline can persist its own representation in parallel; the
// per-job socket feed surfaces in SuggestionsTab via GenerationProgress.
//
// Toast: a window-level CustomEvent is dispatched (no global notify module
// exists in this repo) — host shells already listen for "aidea-studio-toast"
// and forward it to their snackbar.
export const APPLY_SUGGESTION_REQUEST = "APPLY_SUGGESTION_REQUEST";
export const APPLY_SUGGESTION_SUCCESS = "APPLY_SUGGESTION_SUCCESS";

let __applySuggestionItemCounter = 0;
function nextApplyItemId(suggestionId) {
  __applySuggestionItemCounter += 1;
  const sid = suggestionId ? String(suggestionId).slice(0, 12) : "s";
  return `it_ai_${sid}_${__applySuggestionItemCounter}`;
}

function buildItemForKind(kind, suggestionId) {
  // Lazy require avoids a circular import between common/actions and the
  // plugin renderer (which transitively imports common/actions via
  // ItemFrame → InteractivePluginRenderer). Matches the pattern already
  // used for server_utils in this file.
  const {
    defaultParamsFor,
  } = require("../plugins/InteractivePlugin/components/InteractivePluginRenderer");
  const params = defaultParamsFor(kind) || {};
  return {
    id: nextApplyItemId(suggestionId),
    kind,
    ...params,
    // Provenance — survives in pluginToolbarsById.state.items[].source so the
    // workflow tab / element graph can later distinguish AI-seeded items from
    // hand-authored ones, and filter by suggestion id.
    source: "ai",
    suggestionId,
  };
}

export function applySuggestionThunk(suggestion) {
  return (dispatch, getState) => {
    if (!suggestion || typeof suggestion !== "object") return null;
    const shape = suggestion.shape || {};
    const hints = Array.isArray(shape.pluginKindHints) ? shape.pluginKindHints.slice() : [];
    if (shape.hasBranching && !hints.includes("BranchingQuestion")) {
      hints.unshift("BranchingQuestion");
    }
    if (hints.length === 0) return null;

    const state = getState() || {};
    const present = (state.undoGroup && state.undoGroup.present) || state.undoGroup || {};
    const sceneId = present.navItemSelected;
    const scene = sceneId ? (present.navItemsById || {})[sceneId] : null;
    const sceneBoxes = scene && Array.isArray(scene.boxes) ? scene.boxes : [];
    const pluginsById = present.pluginToolbarsById || {};
    const existingBoxId = sceneBoxes.find(
      (bId) => pluginsById[bId] && pluginsById[bId].pluginId === "InteractivePlugin"
    );

    dispatch({
      type: APPLY_SUGGESTION_REQUEST,
      payload: { suggestionId: suggestion.id, kinds: hints },
    });

    let targetBoxId = existingBoxId || null;
    const itemsAdded = [];
    for (const kind of hints) {
      const item = buildItemForKind(kind, suggestion.id);
      itemsAdded.push(item);
      if (!targetBoxId) {
        // seedInteractivePluginBoxAndAddItem returns the new box id; reuse it
        // for the rest of the items so a single suggestion produces ONE
        // InteractivePlugin box on the scene, not N.
        targetBoxId = dispatch(seedInteractivePluginBoxAndAddItem(item));
      } else {
        dispatch(addInteractiveItem(targetBoxId, item));
      }
    }

    dispatch({
      type: APPLY_SUGGESTION_SUCCESS,
      payload: { suggestionId: suggestion.id, boxId: targetBoxId, count: itemsAdded.length },
    });

    // Toast — fire a custom event the host shell can pick up; no-op in tests
    // (no listener). Keeps actions.es6 free of any direct DOM ui coupling.
    if (typeof window !== "undefined" && typeof window.dispatchEvent === "function") {
      try {
        window.dispatchEvent(
          new CustomEvent("aidea-studio-toast", {
            detail: {
              kind: "success",
              message: `Suggestion applied: ${itemsAdded.length} item${itemsAdded.length === 1 ? "" : "s"}`,
              suggestionId: suggestion.id,
            },
          })
        );
      } catch (_) {
        /* ignore — CustomEvent unavailable in some test envs */
      }
    }

    return { boxId: targetBoxId, count: itemsAdded.length };
  };
}
// === /Round 27 ===

// === Round 32 — Workflow Architect from prompt ===
// "Type a topic, get a workflow." Thin thunk over server_utils.
// generateWorkflowFromPrompt: POSTs `{ topic, locale }` to the middleware
// Architect, receives `{ items, variables, triggers }`, and materializes
// the result into the active InteractivePlugin box (auto-seeding one if the
// scene has none yet) + the section's workflow.triggers slot. Each item
// carries `source: "ai"` for provenance, mirroring applySuggestionThunk.
//
// Trigger payloads are normalized so `sceneId` / `pluginBoxId` reflect the
// concrete box we ended up writing items into (the Architect emits abstract
// trigger refs keyed only by sourceItemId / actionTargetItemId).
//
// Toast on success/failure via the same window CustomEvent the rest of the
// studio already dispatches — keeps actions.es6 free of any UI coupling.
export const GENERATE_WORKFLOW_FROM_PROMPT_REQUEST = "GENERATE_WORKFLOW_FROM_PROMPT_REQUEST";
export const GENERATE_WORKFLOW_FROM_PROMPT_SUCCESS = "GENERATE_WORKFLOW_FROM_PROMPT_SUCCESS";
export const GENERATE_WORKFLOW_FROM_PROMPT_FAILURE = "GENERATE_WORKFLOW_FROM_PROMPT_FAILURE";

let __aiWfItemCounter = 0;
function nextAiWfItemId() {
  __aiWfItemCounter += 1;
  return `it_aiwf_${__aiWfItemCounter}`;
}
let __aiWfTriggerCounter = 0;
function nextAiWfTriggerId() {
  __aiWfTriggerCounter += 1;
  return `tg_aiwf_${__aiWfTriggerCounter}`;
}

function emitToast(detail) {
  if (typeof window === "undefined" || typeof window.dispatchEvent !== "function") return;
  try {
    window.dispatchEvent(new CustomEvent("aidea-studio-toast", { detail }));
  } catch (_) {
    /* ignore — CustomEvent unavailable in some test envs */
  }
}

export function generateWorkflowFromPromptThunk(sectionId, prompt, locale) {
  return async (dispatch, getState) => {
    const trimmed = typeof prompt === "string" ? prompt.trim() : "";
    if (!trimmed) return null;

    dispatch({
      type: GENERATE_WORKFLOW_FROM_PROMPT_REQUEST,
      payload: { sectionId, prompt: trimmed, locale: locale || null },
    });

    let result;
    try {
      const { generateWorkflowFromPrompt } = require("./server_utils.es6");
      result = await generateWorkflowFromPrompt(trimmed, locale || null);
    } catch (err) {
      dispatch({
        type: GENERATE_WORKFLOW_FROM_PROMPT_FAILURE,
        payload: { error: String((err && err.message) || err) },
      });
      emitToast({
        kind: "error",
        message: `Workflow generation failed: ${String((err && err.message) || err)}`,
      });
      throw err;
    }

    const items = Array.isArray(result && result.items) ? result.items : [];
    const triggers = Array.isArray(result && result.triggers) ? result.triggers : [];
    if (items.length === 0) {
      dispatch({
        type: GENERATE_WORKFLOW_FROM_PROMPT_FAILURE,
        payload: { error: "Architect returned no items" },
      });
      emitToast({
        kind: "warning",
        message: "Workflow generation returned no items",
      });
      return { itemCount: 0, triggerCount: 0, boxId: null };
    }

    // Discover (or auto-seed) the active scene's InteractivePlugin box.
    const state = getState() || {};
    const present = (state.undoGroup && state.undoGroup.present) || state.undoGroup || {};
    const sceneId = present.navItemSelected;
    const scene = sceneId ? (present.navItemsById || {})[sceneId] : null;
    const sceneBoxes = scene && Array.isArray(scene.boxes) ? scene.boxes : [];
    const pluginsById = present.pluginToolbarsById || {};
    const existingBoxId = sceneBoxes.find(
      (bId) => pluginsById[bId] && pluginsById[bId].pluginId === "InteractivePlugin"
    );

    // Remap incoming abstract item ids → freshly minted runtime ids so a
    // second "Generate" pass doesn't collide with the previous run.
    const idMap = {};
    let targetBoxId = existingBoxId || null;
    for (const incoming of items) {
      if (!incoming || typeof incoming !== "object" || !incoming.kind) continue;
      const runtimeId = nextAiWfItemId();
      if (incoming.id) idMap[incoming.id] = runtimeId;
      const { id: _incomingId, ...rest } = incoming;
      const item = {
        ...rest,
        id: runtimeId,
        source: "ai",
      };
      if (!targetBoxId) {
        targetBoxId = dispatch(seedInteractivePluginBoxAndAddItem(item));
      } else {
        dispatch(addInteractiveItem(targetBoxId, item));
      }
    }

    let triggerCount = 0;
    for (const tg of triggers) {
      if (!tg || typeof tg !== "object") continue;
      const sourceItemId =
        tg.sourceItemId && idMap[tg.sourceItemId] ? idMap[tg.sourceItemId] : tg.sourceItemId;
      const actionTargetItemId =
        tg.actionTargetItemId && idMap[tg.actionTargetItemId]
          ? idMap[tg.actionTargetItemId]
          : tg.actionTargetItemId;
      dispatch(
        addWorkflowTrigger(sectionId, {
          ...tg,
          id: tg.id || nextAiWfTriggerId(),
          sceneId: sceneId || tg.sceneId || null,
          pluginBoxId: targetBoxId || tg.pluginBoxId || null,
          sourceItemId,
          actionTargetItemId,
          actions: Array.isArray(tg.actions) ? tg.actions : [],
        })
      );
      triggerCount += 1;
    }

    dispatch({
      type: GENERATE_WORKFLOW_FROM_PROMPT_SUCCESS,
      payload: {
        sectionId,
        boxId: targetBoxId,
        itemCount: items.length,
        triggerCount,
      },
    });
    emitToast({
      kind: "success",
      message: `Workflow generated: ${items.length} item${items.length === 1 ? "" : "s"}, ${triggerCount} trigger${triggerCount === 1 ? "" : "s"}`,
    });
    return { itemCount: items.length, triggerCount, boxId: targetBoxId };
  };
}
// === /Round 32 ===

// === AI Generate Full Course — multi-scene orchestration ===
// Loops the Workflow Architect endpoint N times for a single topic, producing
// a course-shaped sequence of items + triggers. MVP scope: every call hits
// the same single-scene Architect (/api/workflow-architect/generate); the
// thunk materialises each response into the currently-selected section by
// dispatching the same actions generateWorkflowFromPromptThunk already uses
// (ADD_INTERACTIVE_ITEM via seedInteractivePluginBoxAndAddItem +
// addInteractiveItem, plus addWorkflowTrigger). Server-side multi-scene
// orchestration with proper scene-graph creation can ship later — keeping
// the wiring identical to the single-scene path keeps the UI flow predictable
// for the author while the back end matures.
//
// Per-iteration prompt carries scene index + total count + (when available)
// a coarse summary of the previous response so the architect can vary content
// and add a closing transition into the next scene. Calls run sequentially
// so the architect can't be overloaded and so partial-progress dispatches
// always reach the store in order.
//
// Re-uses the existing generateWorkflowFromPromptThunk under the hood rather
// than duplicating its (auto-seed box, remap ids, rewrite triggers) logic —
// per Q1/Q5 of the design checklist, the thinnest wrapper that earns its
// keep is N sequential dispatches plus a totals envelope.
export const GENERATE_FULL_COURSE_REQUEST = "GENERATE_FULL_COURSE_REQUEST";
export const GENERATE_FULL_COURSE_SCENE_DONE = "GENERATE_FULL_COURSE_SCENE_DONE";
export const GENERATE_FULL_COURSE_SUCCESS = "GENERATE_FULL_COURSE_SUCCESS";
export const GENERATE_FULL_COURSE_FAILURE = "GENERATE_FULL_COURSE_FAILURE";

const FULL_COURSE_MIN_SCENES = 1;
const FULL_COURSE_MAX_SCENES = 10;

function clampSceneCount(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return FULL_COURSE_MIN_SCENES;
  if (x < FULL_COURSE_MIN_SCENES) return FULL_COURSE_MIN_SCENES;
  if (x > FULL_COURSE_MAX_SCENES) return FULL_COURSE_MAX_SCENES;
  return Math.floor(x);
}

export function generateFullCourseThunk(topic, sceneCount, locale) {
  return async (dispatch, getState) => {
    const trimmed = typeof topic === "string" ? topic.trim() : "";
    const n = clampSceneCount(sceneCount);
    if (!trimmed) return null;

    // Resolve the active section once, here: the inner thunk needs a real
    // sectionId so addWorkflowTrigger lands on the right workflow slot.
    // Mirrors CanvasWorkflowPanel.resolveSectionId — if the selected nav
    // item is itself a section, use it; otherwise walk up via .parent.
    const state = getState() || {};
    const present = (state.undoGroup && state.undoGroup.present) || state.undoGroup || {};
    const selectedId = present.navItemSelected;
    const navItemsById = present.navItemsById || {};
    const selected = selectedId != null ? navItemsById[selectedId] : null;
    let activeSectionId = null;
    if (selected) {
      if (selected.type === "section") activeSectionId = selectedId;
      else if (selected.parent !== null && selected.parent !== undefined && selected.parent !== "")
        activeSectionId = selected.parent;
    }

    dispatch({
      type: GENERATE_FULL_COURSE_REQUEST,
      payload: { topic: trimmed, sceneCount: n, locale: locale || null },
    });

    const sceneResults = [];
    let previousSummary = "";
    try {
      for (let idx = 0; idx < n; idx += 1) {
        const isLast = idx === n - 1;
        const sceneLabel = `Scene ${idx + 1} of ${n}`;
        const transitionHint = isLast
          ? "End the course."
          : "End this scene with a clear transition into the next scene.";
        const prevHint = previousSummary ? ` Previous scene produced: ${previousSummary}.` : "";
        const scenePrompt = `${trimmed} — ${sceneLabel}.${prevHint} ${transitionHint}`.trim();

        // Chain the established single-scene thunk. It owns server_utils
        // require, auto-seeding the InteractivePlugin box, id remapping,
        // trigger rewriting, and toast emission. Sequential by design — we
        // want the previous round's items in the store before the next
        // architect call so the previousSummary stays accurate.
        // eslint-disable-next-line no-await-in-loop -- sequential by design
        const result = await dispatch(
          generateWorkflowFromPromptThunk(activeSectionId, scenePrompt, locale || null)
        );
        const safeResult = result || { itemCount: 0, triggerCount: 0, boxId: null };
        sceneResults.push(safeResult);
        dispatch({
          type: GENERATE_FULL_COURSE_SCENE_DONE,
          payload: {
            sceneIndex: idx,
            sceneCount: n,
            itemCount: safeResult.itemCount,
            triggerCount: safeResult.triggerCount,
          },
        });
        previousSummary = `${safeResult.itemCount} interactive item${safeResult.itemCount === 1 ? "" : "s"}`;
      }
    } catch (err) {
      dispatch({
        type: GENERATE_FULL_COURSE_FAILURE,
        payload: { error: String((err && err.message) || err) },
      });
      emitToast({
        kind: "error",
        message: `Course generation failed: ${String((err && err.message) || err)}`,
      });
      throw err;
    }

    const totals = sceneResults.reduce(
      (acc, r) => ({
        itemCount: acc.itemCount + (r.itemCount || 0),
        triggerCount: acc.triggerCount + (r.triggerCount || 0),
      }),
      { itemCount: 0, triggerCount: 0 }
    );
    dispatch({
      type: GENERATE_FULL_COURSE_SUCCESS,
      payload: { sceneCount: n, ...totals, sceneResults },
    });
    emitToast({
      kind: "success",
      message: `Course generated: ${n} scene${n === 1 ? "" : "s"}, ${totals.itemCount} item${totals.itemCount === 1 ? "" : "s"}`,
    });
    return { sceneCount: n, ...totals, sceneResults };
  };
}
// === /AI Generate Full Course ===

// === Interactivity Studio (#4) ===
export const OPEN_INTERACTIVITY_STUDIO = "OPEN_INTERACTIVITY_STUDIO";
export const CLOSE_INTERACTIVITY_STUDIO = "CLOSE_INTERACTIVITY_STUDIO";
export const SET_INTERACTIVITY_STUDIO_TAB = "SET_INTERACTIVITY_STUDIO_TAB";
export const SET_INTERACTIVITY_STUDIO_BUSY = "SET_INTERACTIVITY_STUDIO_BUSY";

export function openInteractivityStudio({ tab }) {
  return { type: OPEN_INTERACTIVITY_STUDIO, payload: { tab } };
}
export function closeInteractivityStudio() {
  return { type: CLOSE_INTERACTIVITY_STUDIO };
}
export function setInteractivityStudioTab(tab) {
  return { type: SET_INTERACTIVITY_STUDIO_TAB, payload: { tab } };
}
export function setInteractivityStudioBusy(busy) {
  return { type: SET_INTERACTIVITY_STUDIO_BUSY, payload: { busy } };
}

// === Suggestions on-demand (#2) ===
export const REGENERATE_SUGGESTIONS_REQUEST = "REGENERATE_SUGGESTIONS_REQUEST";
export const REGENERATE_SUGGESTIONS_SUCCESS = "REGENERATE_SUGGESTIONS_SUCCESS";
export const REGENERATE_SUGGESTIONS_FAILURE = "REGENERATE_SUGGESTIONS_FAILURE";

export function regenerateSuggestionsThunk(trainingId, scope, sceneId) {
  return async (dispatch) => {
    dispatch({
      type: REGENERATE_SUGGESTIONS_REQUEST,
      payload: { trainingId, scope, sceneId },
    });
    try {
      const { regenerateSuggestions } = require("./server_utils.es6");
      const { jobId, room } = await regenerateSuggestions(trainingId, scope, sceneId);
      dispatch(setInteractivityStudioBusy({ jobId, scope, sceneId: sceneId || null }));
      dispatch({
        type: REGENERATE_SUGGESTIONS_SUCCESS,
        payload: { jobId, room, scope, sceneId },
      });
      return { jobId, room };
    } catch (err) {
      dispatch({
        type: REGENERATE_SUGGESTIONS_FAILURE,
        payload: { error: String((err && err.message) || err) },
      });
      throw err;
    }
  };
}

// === Generate Workflow (#3) ===
export const GENERATE_BASELINE_WORKFLOW_START = "GENERATE_BASELINE_WORKFLOW_START";
export const GENERATE_BASELINE_WORKFLOW_DONE = "GENERATE_BASELINE_WORKFLOW_DONE";

export function generateBaselineWorkflowAction() {
  return (dispatch, getState) => {
    const { planTrainingBaseline } = require("../_editor/components/workflow/planTrainingBaseline");
    const present = (getState().undoGroup && getState().undoGroup.present) || {};
    const plan = planTrainingBaseline(
      present.navItemsById || {},
      present.boxesById || {},
      present.pluginToolbarsById || {}
    );
    dispatch({ type: GENERATE_BASELINE_WORKFLOW_START, payload: { stats: plan.stats } });
    for (const [sectionId, workflow] of Object.entries(plan.perSection)) {
      if (!workflow) continue;
      dispatch(
        mergeWorkflowProposal(sectionId, {
          variables: workflow.variables,
          triggers: workflow.triggers,
        })
      );
    }
    dispatch({ type: GENERATE_BASELINE_WORKFLOW_DONE, payload: { stats: plan.stats } });
  };
}

// === Task 5 — canvas-first interactive items ===
export const ADD_INTERACTIVE_ITEM = "ADD_INTERACTIVE_ITEM";
export const UPDATE_ITEM_POSITION = "UPDATE_ITEM_POSITION";
export const UPDATE_ITEM_PARAM = "UPDATE_ITEM_PARAM";
export const REMOVE_INTERACTIVE_ITEM = "REMOVE_INTERACTIVE_ITEM";
export const SELECT_INTERACTIVE_ITEM = "SELECT_INTERACTIVE_ITEM";

export const addInteractiveItem = (boxId, item) => ({
  type: ADD_INTERACTIVE_ITEM,
  payload: { boxId, item },
});
export const updateItemPosition = (boxId, itemId, position) => ({
  type: UPDATE_ITEM_POSITION,
  payload: { boxId, itemId, position },
});
export const updateItemParam = (boxId, itemId, path, value) => ({
  type: UPDATE_ITEM_PARAM,
  payload: { boxId, itemId, path, value },
});
export const removeInteractiveItem = (boxId, itemId) => ({
  type: REMOVE_INTERACTIVE_ITEM,
  payload: { boxId, itemId },
});
export const selectInteractiveItem = (boxId, itemId) => ({
  type: SELECT_INTERACTIVE_ITEM,
  payload: { boxId, itemId },
});

export const SET_INTERACTIVE_MODE = "SET_INTERACTIVE_MODE";
export const setInteractiveMode = (mode) => ({
  type: SET_INTERACTIVE_MODE,
  payload: { mode },
});

// Auto-seed an InteractivePlugin box on the active scene, then add an item to
// it. Used by InteractiveLibraryRail when a user clicks a pill before an
// InteractivePlugin container exists on the scene. Deterministic counter-based
// box id (no Date.now/Math.random) so tests and replays are stable.
let __interactiveBoxCounter = 0;
function nextInteractiveBoxId() {
  __interactiveBoxCounter += 1;
  return `bo-ip_${__interactiveBoxCounter}`;
}
export const seedInteractivePluginBoxAndAddItem = (item) => (dispatch, getState) => {
  const state = getState() || {};
  const present = (state.undoGroup && state.undoGroup.present) || state.undoGroup || {};
  const sceneId = present.navItemSelected;
  const newBoxId = nextInteractiveBoxId();
  // Payload shape mirrors what InteractivePlugin.getInitialParams would
  // produce via createBox: pluginToolbarsById picks pluginId off
  // payload.initialParams.name; nav_items_by_id appends ids.id to
  // boxes[]; boxes_by_id stores the new box keyed by ids.id.
  dispatch(
    addBox(
      {
        id: newBoxId,
        parent: sceneId,
        container: 0,
        col: 0,
        row: 0,
        position: { x: 0, y: 0, type: "absolute" },
        page: sceneId,
      },
      true,
      true,
      {},
      {},
      { items: [] },
      undefined,
      { name: "InteractivePlugin", width: "100%", height: "100%" }
    )
  );
  dispatch(addInteractiveItem(newBoxId, item));
  return newBoxId;
};
// === /Task 5 ===

// === Template system bootstrap (plan §5) ===
// Counter-derived ids so multiple applications of the same template on the
// same scene don't collide. Deterministic across one module load → tests +
// redux-undo replays are stable.
let __templateItemCounter = 0;
function nextTemplateItemId() {
  __templateItemCounter += 1;
  return `tpl_it_${__templateItemCounter}`;
}
let __templateTriggerCounter = 0;
function nextTemplateTriggerId() {
  __templateTriggerCounter += 1;
  return `tpl_tg_${__templateTriggerCounter}`;
}

// Rewrite occurrences of {{itemId:<local-id>}} inside an action object to the
// minted item id from idMap. Shallow walk — actions are always one level deep
// in the seed catalog. Unknown symbols are left as-is so a malformed template
// fails loudly rather than silently.
function rewriteActionRefs(action, idMap) {
  if (!action || typeof action !== "object") return action;
  const next = {};
  for (const k of Object.keys(action)) {
    const v = action[k];
    if (typeof v === "string") {
      const m = v.match(/^\{\{itemId:(.+)\}\}$/);
      next[k] = m && idMap[m[1]] ? idMap[m[1]] : v;
    } else {
      next[k] = v;
    }
  }
  return next;
}

// Apply a seed template to a given (sectionId, sceneId, boxId). Dispatches
// ADD_INTERACTIVE_ITEM for each template item + addWorkflowTrigger for each
// trigger, in declaration order. Template-local item ids in triggers are
// rewired to the freshly generated runtime ids so multiple applications stay
// disjoint.
export function applyTemplateThunk(sectionId, sceneId, boxId, template) {
  return (dispatch, getState) => {
    if (!template || typeof template !== "object") return null;
    const {
      defaultParamsFor,
    } = require("../plugins/InteractivePlugin/components/InteractivePluginRenderer");
    const items = Array.isArray(template.items) ? template.items : [];
    const triggers = Array.isArray(template.triggers) ? template.triggers : [];

    // If no box target yet, create an empty InteractivePlugin box now so the
    // template items land correctly without a stale sentinel polluting the list.
    let targetBoxId = boxId;
    if (!targetBoxId) {
      const state = getState();
      const present = (state && state.undoGroup && state.undoGroup.present) || {};
      const navSceneId = present.navItemSelected || sceneId;
      targetBoxId = nextInteractiveBoxId();
      dispatch(
        addBox(
          {
            id: targetBoxId,
            parent: navSceneId,
            container: 0,
            col: 0,
            row: 0,
            position: { x: 0, y: 0, type: "absolute" },
            page: navSceneId,
          },
          true,
          true,
          {},
          {},
          { items: [] },
          undefined,
          { name: "InteractivePlugin", width: "100%", height: "100%" }
        )
      );
    }

    // Pass 1 — mint ALL runtime IDs before dispatching any item so that
    // cross-item refs (e.g. onCorrectShowItemId: "{{itemId:img-correct}}")
    // can be resolved even when the referenced item appears later in the list.
    const idMap = {};
    for (const tplItem of items) {
      if (!tplItem || typeof tplItem !== "object" || !tplItem.kind) continue;
      const runtimeId = nextTemplateItemId();
      if (tplItem.id) idMap[tplItem.id] = runtimeId;
    }

    // Deep-rewrite any {{itemId:X}} references — handles strings, arrays, and nested objects.
    // This covers both plain string params (onCorrectShowItemId) and action-array params
    // (onCorrectActions: [{ kind: "showItem", itemId: "{{itemId:X}}" }]).
    function rewriteDeep(v) {
      if (typeof v === "string") {
        const m = v.match(/^\{\{itemId:(.+)\}\}$/);
        return m && idMap[m[1]] ? idMap[m[1]] : v;
      }
      if (Array.isArray(v)) return v.map(rewriteDeep);
      if (v && typeof v === "object") {
        const out = {};
        for (const [k, val] of Object.entries(v)) out[k] = rewriteDeep(val);
        return out;
      }
      return v;
    }
    function rewriteOverrides(obj) {
      if (!obj || typeof obj !== "object") return obj;
      const out = {};
      for (const [k, v] of Object.entries(obj)) {
        out[k] = rewriteDeep(v);
      }
      return out;
    }

    // Pass 2 — dispatch items with all refs resolved.
    for (const tplItem of items) {
      if (!tplItem || typeof tplItem !== "object" || !tplItem.kind) continue;
      const params = defaultParamsFor(tplItem.kind) || {};
      const runtimeId = idMap[tplItem.id];
      if (!runtimeId) continue;
      // eslint-disable-next-line no-unused-vars
      const { id: _tplId, kind, ...overrides } = tplItem;
      dispatch(
        addInteractiveItem(targetBoxId, {
          ...params,
          ...rewriteOverrides(overrides),
          id: runtimeId,
          kind,
          source: "template",
          templateId: template.id,
        })
      );
    }

    for (const tg of triggers) {
      if (!tg || typeof tg !== "object") continue;
      const actions = Array.isArray(tg.actions)
        ? tg.actions.map((a) => rewriteActionRefs(a, idMap))
        : [];
      dispatch(
        addWorkflowTrigger(sectionId, {
          ...tg,
          id: nextTemplateTriggerId(),
          sceneId: sceneId || null,
          pluginBoxId: targetBoxId || null,
          on: tg.on || "onItemCompleted",
          actions,
        })
      );
    }
    return { itemCount: items.length, triggerCount: triggers.length };
  };
}
// === /Template system bootstrap ===

// === Save-as-template — capture current scene as a reusable user template ===
// Symmetry note: the captured payload mirrors the seed template shape so
// applyTemplateThunk can consume both seed and user templates with no branching.
// Persistence is a localStorage backup keyed by USER_TEMPLATES_STORAGE_KEY;
// the redux slice (reducers/user_templates.es6) is the source of truth at
// runtime, and the LOAD_USER_TEMPLATES_FROM_STORAGE action rehydrates it on
// editor mount.
export const ADD_USER_TEMPLATE = "ADD_USER_TEMPLATE";
export const REMOVE_USER_TEMPLATE = "REMOVE_USER_TEMPLATE";
export const LOAD_USER_TEMPLATES_FROM_STORAGE = "LOAD_USER_TEMPLATES_FROM_STORAGE";

export const USER_TEMPLATES_STORAGE_KEY = "aidea-user-templates";

// Monotonic counter for user-template ids so two saves within the same session
// can't collide on `Date.now()` granularity. Persisted into localStorage with
// the templates themselves so re-mounts continue minting unique ids.
let __userTemplateCounter = 0;
function nextUserTemplateId() {
  __userTemplateCounter += 1;
  return `tpl-user-${__userTemplateCounter}`;
}

function safeReadStorage(key) {
  try {
    if (typeof window === "undefined" || !window.localStorage) return null;
    const raw = window.localStorage.getItem(key);
    return raw == null ? null : JSON.parse(raw);
  } catch (e) {
    return null;
  }
}
function safeWriteStorage(key, value) {
  try {
    if (typeof window === "undefined" || !window.localStorage) return;
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    // Quota exceeded, private mode, etc. — swallow; the in-memory slice
    // still works, and the next save retries.
  }
}

export function addUserTemplate(template) {
  return { type: ADD_USER_TEMPLATE, payload: { template } };
}
export function removeUserTemplate(id) {
  return { type: REMOVE_USER_TEMPLATE, payload: { id } };
}
export function loadUserTemplatesFromStorage(templates) {
  return { type: LOAD_USER_TEMPLATES_FROM_STORAGE, payload: { templates } };
}

// Read the active scene's InteractivePlugin items + section.workflow.triggers
// and stash them as a user template. Returns the captured template object
// (or null if there's nothing to capture).
export function saveSceneAsTemplateThunk(name, description) {
  return (dispatch, getState) => {
    const state = getState() || {};
    const present = (state.undoGroup && state.undoGroup.present) || state.undoGroup || {};
    const navItemsById = present.navItemsById || {};
    const pluginsById = present.pluginToolbarsById || {};
    const sceneId = present.navItemSelected || null;
    const scene = sceneId ? navItemsById[sceneId] : null;
    if (!scene) return null;
    // Walk the scene's boxes to find the InteractivePlugin container — same
    // mapping the rail uses (pluginToolbarsById[boxId].pluginId is authoritative,
    // not boxesById[id].content).
    const sceneBoxes = Array.isArray(scene.boxes) ? scene.boxes : [];
    const ipBoxId = sceneBoxes.find(
      (bId) => pluginsById[bId] && pluginsById[bId].pluginId === "InteractivePlugin"
    );
    const ipState = (ipBoxId && pluginsById[ipBoxId] && pluginsById[ipBoxId].state) || {};
    const items = Array.isArray(ipState.items) ? ipState.items : [];
    // Section workflow lives on the parent section. If the active nav item is
    // itself a section, use it; otherwise climb via .parent.
    let sectionId = null;
    if (scene.type === "section") sectionId = sceneId;
    else if (scene.parent != null) sectionId = scene.parent;
    const section = sectionId ? navItemsById[sectionId] : null;
    const triggers =
      section && section.workflow && Array.isArray(section.workflow.triggers)
        ? section.workflow.triggers
        : [];
    // Filter triggers to ones that belong to this scene/box so the template
    // doesn't drag in unrelated triggers from sibling scenes in the same section.
    const sceneTriggers = triggers.filter(
      (t) => t && (t.sceneId === sceneId || (ipBoxId && t.pluginBoxId === ipBoxId))
    );

    const template = {
      id: nextUserTemplateId(),
      title: (name && String(name).trim()) || "Untitled template",
      description: (description && String(description).trim()) || "",
      icon: "bookmark",
      // Deep-clone via JSON round-trip so future scene edits don't mutate the
      // captured snapshot. Items/triggers are pure JSON in this codebase.
      items: JSON.parse(JSON.stringify(items)),
      triggers: JSON.parse(JSON.stringify(sceneTriggers)),
      source: "user",
      createdAt: new Date().toISOString(),
    };
    dispatch(addUserTemplate(template));
    // Persist by reading the current slice AFTER dispatch so the backup
    // mirrors the redux store exactly.
    const post = getState();
    const list = (post && post.userTemplates) || [];
    safeWriteStorage(USER_TEMPLATES_STORAGE_KEY, list);
    return template;
  };
}

// Companion thunk for the rail's × delete affordance — mirrors save by
// writing the post-dispatch slice back to localStorage.
export function removeUserTemplateThunk(id) {
  return (dispatch, getState) => {
    dispatch(removeUserTemplate(id));
    const post = getState();
    const list = (post && post.userTemplates) || [];
    safeWriteStorage(USER_TEMPLATES_STORAGE_KEY, list);
  };
}

// Editor-mount hook: read the localStorage backup into redux. Called from
// EditorApp / ReduxProvider on bootstrap. No-op if nothing is stored.
export function rehydrateUserTemplatesThunk() {
  return (dispatch) => {
    const stored = safeReadStorage(USER_TEMPLATES_STORAGE_KEY);
    if (!Array.isArray(stored) || stored.length === 0) return;
    // Advance the counter past any persisted ids so freshly-minted ids stay
    // unique across reloads.
    for (const t of stored) {
      const m = t && typeof t.id === "string" && t.id.match(/^tpl-user-(\d+)$/);
      if (m) {
        const n = parseInt(m[1], 10);
        if (Number.isFinite(n) && n > __userTemplateCounter) __userTemplateCounter = n;
      }
    }
    dispatch(loadUserTemplatesFromStorage(stored));
  };
}
// === /Save-as-template ===

// === Backward-compat wrappers (kept so legacy callers don't break) ===
export function openWorkflowOverlay() {
  return openInteractivityStudio({ tab: "workflow" });
}
export function closeWorkflowOverlay() {
  return closeInteractivityStudio();
}
export function openFlowViewer() {
  return openInteractivityStudio({ tab: "flow-viewer" });
}
export function closeFlowViewer() {
  return closeInteractivityStudio();
}
export function openWorkflowSuggestionsForCurrentScene(/* sceneId */) {
  return openInteractivityStudio({ tab: "suggestions" });
}

// === Phase B Task 4 — docked CanvasWorkflowPanel toggle ===
// Tiny ephemeral toggle; the slice lives in reducers/canvas_workflow.es6.
export const TOGGLE_CANVAS_WORKFLOW_DOCKED = "TOGGLE_CANVAS_WORKFLOW_DOCKED";
export const toggleCanvasWorkflowDocked = () => ({ type: TOGGLE_CANVAS_WORKFLOW_DOCKED });

// === Round 27 Item 11 — expanded (double-width) CanvasWorkflowPanel toggle ===
// Flips canvas_workflow.expanded. When true, the panel's flex-basis/width
// doubles via a .canvas-workflow-panel--expanded SCSS modifier so authors
// can see more of the graph without leaving the editor for the modal.
export const TOGGLE_CANVAS_WORKFLOW_EXPANDED = "TOGGLE_CANVAS_WORKFLOW_EXPANDED";
export const toggleCanvasWorkflowExpanded = () => ({ type: TOGGLE_CANVAS_WORKFLOW_EXPANDED });

// === MiniMap toggle — controls visibility of the React Flow MiniMap overlay
// in CanvasWorkflowPanel. Defaults to visible (true) so large workflows are
// navigable out of the box; the panel header has an eye button that flips it.
export const TOGGLE_CANVAS_WORKFLOW_MINIMAP = "TOGGLE_CANVAS_WORKFLOW_MINIMAP";
export const toggleCanvasWorkflowMinimap = () => ({ type: TOGGLE_CANVAS_WORKFLOW_MINIMAP });

// === Round 25 follow-up — persistent workflow node positions ===
// Closes user item 2 from Round 24: nodes in the panel were made draggable
// but their positions reset on every rebuild because buildElementGraph
// recomputes layout from scratch. SET_WORKFLOW_NODE_POSITION saves a single
// node's {x, y} into canvas_workflow.nodePositions[nodeId]; the panel
// overlays the saved map on top of builder defaults so author-tuned layout
// survives item/trigger edits. CLEAR_WORKFLOW_NODE_POSITIONS wipes the map
// (backs the header "Reset positions" button) so the next render falls
// back to the builder's layout.
export const SET_WORKFLOW_NODE_POSITION = "SET_WORKFLOW_NODE_POSITION";
export const setWorkflowNodePosition = (nodeId, position) => ({
  type: SET_WORKFLOW_NODE_POSITION,
  payload: { nodeId, position: { x: position.x, y: position.y } },
});
export const CLEAR_WORKFLOW_NODE_POSITIONS = "CLEAR_WORKFLOW_NODE_POSITIONS";
export const clearWorkflowNodePositions = () => ({ type: CLEAR_WORKFLOW_NODE_POSITIONS });

// === Inline edge label override (canvas workflow panel) ===
// Edge labels in the workflow panel are derived from triggers (e.g. "correct",
// "React (correct)", alternative labels). Authors couldn't override them, so
// these actions store a per-edge UI label override in
// canvas_workflow.edgeLabels[edgeId]. The trigger's underlying data is NOT
// mutated — DeletableEdge renders customLabel || originalLabel.
export const SET_EDGE_LABEL = "SET_EDGE_LABEL";
export const setEdgeLabel = (edgeId, label) => ({
  type: SET_EDGE_LABEL,
  payload: { edgeId, label },
});
export const CLEAR_EDGE_LABEL = "CLEAR_EDGE_LABEL";
export const clearEdgeLabel = (edgeId) => ({
  type: CLEAR_EDGE_LABEL,
  payload: { edgeId },
});

// === Phase C item 6 — canvas preview frame (workflow node click scrub) ===
// Ephemeral, non-undoable. SET_PREVIEW_FRAME hard-hides scene boxes whose
// timing range does not include the frame; CLEAR_PREVIEW_FRAME restores
// normal authoring view. See reducers/canvas_preview.es6.
export const SET_PREVIEW_FRAME = "SET_PREVIEW_FRAME";
export const CLEAR_PREVIEW_FRAME = "CLEAR_PREVIEW_FRAME";
export const setPreviewFrame = (frame, sourceNodeId = null) => ({
  type: SET_PREVIEW_FRAME,
  payload: { frame, sourceNodeId },
});
export const clearPreviewFrame = () => ({ type: CLEAR_PREVIEW_FRAME });

// === Translate Scene ===
// Thunk that POSTs every InteractivePlugin item on a scene to the middleware
// /api/workflow/translate-scene endpoint, then dispatches one
// UPDATE_ITEM_PARAM per translated item to replace its `params` wholesale
// with the translated version. Path is `["params"]` and value is the entire
// translated params object — the per-kind schema on the middleware side
// guarantees the shape matches what each itemKind renderer expects.
//
// Item enumeration mirrors selectSectionWorkflow.compute(): walk
// navItemsById[sceneId].boxes[], read pluginToolbarsById[boxId].state.items[]
// for each box, and remember which boxId each item came from so the
// dispatched UPDATE_ITEM_PARAM can target the right pluginToolbar row.
//
// `targetLocale` is "tr" / "es" / "en" (the locales i18n.js ships); the
// caller is the TranslatePicker in WorkflowTab.
export function translateSceneThunk({ sectionId, sceneId, targetLocale } = {}) {
  return async (dispatch, getState) => {
    if (sectionId === null || sectionId === undefined) return [];
    if (sceneId === null || sceneId === undefined) return [];
    if (!targetLocale) return [];

    const state = getState() || {};
    const present = (state.undoGroup && state.undoGroup.present) || {};
    const navItemsById = present.navItemsById || {};
    const pluginToolbarsById = present.pluginToolbarsById || {};

    const scene = navItemsById[sceneId];
    if (!scene || !Array.isArray(scene.boxes)) return [];

    // itemId -> boxId so we can dispatch UPDATE_ITEM_PARAM with the right
    // target after the server response comes back. Walk boxes in scene order.
    const boxIdByItemId = new Map();
    const itemsForRequest = [];
    scene.boxes.forEach((boxId) => {
      const tb = pluginToolbarsById[boxId];
      if (!tb || tb.pluginId !== "InteractivePlugin") return;
      const itemList = (tb.state && Array.isArray(tb.state.items) && tb.state.items) || [];
      itemList.forEach((it) => {
        if (!it || !it.id) return;
        const kind = it.kind;
        // `params` is the wrapped shape per the canvas-first plan; legacy
        // items that still flatten their fields at the top level fall back
        // to the item itself so the request still contains something useful.
        const params = it.params && typeof it.params === "object" ? it.params : null;
        if (!kind) return;
        boxIdByItemId.set(it.id, boxId);
        itemsForRequest.push({
          itemId: it.id,
          kind,
          params: params || {},
        });
      });
    });

    if (itemsForRequest.length === 0) return [];

    // Lazy require avoids pulling the http_service chain into the action file
    // at module-eval time (matches the pattern already used for server_utils
    // and InteractivePluginRenderer in this file).
    const { translateScene } = require("../_editor/services/translateSceneService");

    let translated;
    try {
      translated = await translateScene({
        sectionId,
        sceneId,
        targetLocale,
        items: itemsForRequest,
      });
    } catch (err) {
      if (typeof window !== "undefined" && typeof window.CustomEvent === "function") {
        window.dispatchEvent(
          new window.CustomEvent("aidea-studio-toast", {
            detail: {
              kind: "error",
              message: `Translation failed: ${err && err.message ? err.message : String(err)}`,
            },
          })
        );
      }
      return [];
    }

    const dispatched = [];
    (Array.isArray(translated) ? translated : []).forEach((entry) => {
      if (!entry || !entry.itemId || !entry.params) return;
      const boxId = boxIdByItemId.get(entry.itemId);
      if (!boxId) return;
      // Wholesale replace the whole params object. The reducer's path-walk
      // (plugin_toolbars_by_id.es6 UPDATE_ITEM_PARAM) clones each level and
      // assigns the final value, so this is one-shot atomic per item.
      dispatch(updateItemParam(boxId, entry.itemId, ["params"], entry.params));
      dispatched.push({ boxId, itemId: entry.itemId });
    });
    return dispatched;
  };
}
// === /Translate Scene ===

// =============================================================================
// === Scenario-based Interactive System =======================================
// =============================================================================

// --- Selection (interactivity_studio reducer) ---
export const SELECT_SCENE = "SELECT_SCENE";
export const selectScene = (boxId, sceneId) => ({
  type: SELECT_SCENE,
  payload: { boxId, sceneId },
});

export const SELECT_SCENARIO_ELEMENT = "SELECT_SCENARIO_ELEMENT";
export const selectScenarioElement = (boxId, sceneId, elementId) => ({
  type: SELECT_SCENARIO_ELEMENT,
  payload: { boxId, sceneId, elementId },
});

// --- Scenario meta (plugin_toolbars_by_id reducer) ---
export const UPDATE_SCENARIO_META = "UPDATE_SCENARIO_META";
export const updateScenarioMeta = (boxId, patch) => ({
  type: UPDATE_SCENARIO_META,
  payload: { boxId, patch },
});

// --- Variables (plugin_toolbars_by_id reducer) ---
export const SET_SCENARIO_VARIABLES = "SET_SCENARIO_VARIABLES";
export const setScenarioVariables = (boxId, variables) => ({
  type: SET_SCENARIO_VARIABLES,
  payload: { boxId, variables },
});

// --- Scenes (plugin_toolbars_by_id reducer) ---
export const ADD_SCENE = "ADD_SCENE";
export const addScene = (boxId, scene) => ({
  type: ADD_SCENE,
  payload: { boxId, scene },
});

export const REMOVE_SCENE = "REMOVE_SCENE";
export const removeScene = (boxId, sceneId) => ({
  type: REMOVE_SCENE,
  payload: { boxId, sceneId },
});

export const UPDATE_SCENE = "UPDATE_SCENE";
export const updateScene = (boxId, sceneId, patch) => ({
  type: UPDATE_SCENE,
  payload: { boxId, sceneId, patch },
});

// --- Elements (plugin_toolbars_by_id reducer) ---
export const ADD_SCENARIO_ELEMENT = "ADD_SCENARIO_ELEMENT";
export const addScenarioElement = (boxId, sceneId, element) => ({
  type: ADD_SCENARIO_ELEMENT,
  payload: { boxId, sceneId, element },
});

export const REMOVE_SCENARIO_ELEMENT = "REMOVE_SCENARIO_ELEMENT";
export const removeScenarioElement = (boxId, sceneId, elementId) => ({
  type: REMOVE_SCENARIO_ELEMENT,
  payload: { boxId, sceneId, elementId },
});

export const UPDATE_SCENARIO_ELEMENT = "UPDATE_SCENARIO_ELEMENT";
// path: string[] — JSON path within the element to update, e.g. ['question']
// or ['choices', 1, 'text']
export const updateScenarioElement = (boxId, sceneId, elementId, path, value) => ({
  type: UPDATE_SCENARIO_ELEMENT,
  payload: { boxId, sceneId, elementId, path, value },
});

// Thunk: resolve current scene from interactivityStudio and dispatch
// addScenarioElement. Falls back to scenario.startSceneId if no scene selected.
export const addScenarioElementToCurrentScene = (boxId, element) => (dispatch, getState) => {
  const state = getState();
  const is = state.interactivityStudio;
  let sceneId = is && is.selectedSceneId;

  if (!sceneId) {
    const present = (state.undoGroup && state.undoGroup.present) || state;
    const tb = present.pluginToolbarsById && present.pluginToolbarsById[boxId];
    const scenario = tb && tb.state && tb.state.scenario;
    sceneId =
      scenario &&
      (scenario.startSceneId || (scenario.scenes && scenario.scenes[0] && scenario.scenes[0].id));
  }

  if (sceneId) {
    dispatch(addScenarioElement(boxId, sceneId, element));
  }
};

// === /Scenario-based Interactive System =====================================
